import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  LabelList
} from 'recharts';
import '../styles/fmp.css';
import '../styles/planejamentoOrcamentario.css';
import { notificar } from '../utils/notify';
import ModalHistoricoFornecedor from '../components/ModalHistoricoFornecedor';
import ModalEmailNegociacao from '../components/ModalEmailNegociacao';

const formatarValor = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(parseFloat(valor));
};

// No Conta Azul o "código" da categoria é um UUID — não deve aparecer na tela.
const _ehUUID = (s) => typeof s === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());
const codCat = (codigo) => (_ehUUID(codigo) ? '' : (codigo || ''));

const formatarInputBRL = (value) => {
  let raw = value.replace(/[^\d,]/g, '');
  const parts = raw.split(',');
  let intPart = parts[0].replace(/^0+(?=\d)/, ''); // remove zeros à esquerda, mas mantém um único "0"
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return parts.length > 1 ? `${intPart},${parts[1].slice(0, 2)}` : intPart;
};

const InfoTip = ({ texto, posicao = 'top' }) => {
  const [vis, setVis] = React.useState(false);
  const isTop = posicao !== 'bottom';
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: '5px', verticalAlign: 'middle' }}
      onMouseEnter={() => setVis(true)}
      onMouseLeave={() => setVis(false)}
    >
      <i className="fas fa-info-circle" style={{ color: '#0dcaf0', cursor: 'help', fontSize: '13px', filter: 'drop-shadow(0 0 3px rgba(13,202,240,0.5))' }}></i>
      {vis && (
        <div style={{
          position: 'absolute',
          [isTop ? 'bottom' : 'top']: '130%',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#1a1a2e',
          color: '#e0e0e0',
          padding: '9px 13px',
          borderRadius: '8px',
          fontSize: '12px',
          lineHeight: '1.5',
          whiteSpace: 'normal',
          width: '220px',
          zIndex: 99999,
          boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
          pointerEvents: 'none',
          border: '1px solid rgba(13,202,240,0.3)',
          textAlign: 'left',
        }}>
          {texto}
          <div style={{
            position: 'absolute',
            [isTop ? 'top' : 'bottom']: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            border: '5px solid transparent',
            [isTop ? 'borderBottomColor' : 'borderTopColor']: '#1a1a2e',
          }}></div>
        </div>
      )}
    </span>
  );
};

// ── Tooltip dos gráficos (branco, mesma linguagem po-*) ─────────────────────
const TooltipCard = ({ titulo, children }) => (
  <div className="po-tip">
    <div className="po-tip-t">{titulo}</div>
    {children}
  </div>
);

// `rest`: marcador tracejado (orçado/meta ainda não realizado)
const TooltipLinha = ({ cor, rotulo, valor, destaque, rest }) => (
  <div className="po-tip-l">
    <span><i className={rest ? 'rest' : ''} style={rest ? undefined : { background: cor }}></i>{rotulo}</span>
    <b className={destaque ? 'forte' : ''}>{formatarValor(valor)}</b>
  </div>
);

const TooltipBarra = ({ rotulo, pct, cor }) => (
  <div className="po-tip-b">
    <div className="t"><span>{rotulo}</span><b>{pct.toFixed(0)}%</b></div>
    <div className="po-bar"><i style={{ width: `${Math.min(pct, 100)}%`, background: cor }}></i></div>
  </div>
);

// Legenda em chips: marcador + série + total no ano. Passar o mouse num chip
// acende só aquela série no gráfico (destaque). `rest` = marcador tracejado.
const LegendaGrafico = ({ series, destaque, setDestaque }) => {
  const total = series.reduce((s, x) => s + (x.total || 0), 0);
  return (
    <div className="po-legend">
      {series.map((s) => (
        <button
          key={s.key}
          type="button"
          className={(!destaque || destaque === s.key) ? '' : 'apagada'}
          onMouseEnter={() => setDestaque(s.key)}
          onMouseLeave={() => setDestaque(null)}
          onFocus={() => setDestaque(s.key)}
          onBlur={() => setDestaque(null)}
        >
          <i className={s.rest ? 'rest' : ''} style={s.rest ? undefined : { background: s.cor }}></i>
          {s.nome} <b>{formatarValor(s.total)}</b>
          {total > 0 && <small>{((s.total / total) * 100).toFixed(0)}%</small>}
        </button>
      ))}
    </div>
  );
};

// Eixo e rótulos em milhar: 291000 → "R$ 291k" (eixo) / "291k" (topo da barra)
const eixoK = (v) => (v >= 1e6 ? `R$ ${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M` : `R$ ${Math.round(v / 1000)}k`);
const rotuloK = (v) => (v >= 1e6 ? `${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M` : `${Math.round(v / 1000)}k`);

// Segmento da barra empilhada: topo arredondado só quando é o último segmento com
// valor do mês (`topoSe(payload)`); `tracejada` desenha o orçado/meta não realizado
// com contorno tracejado. Recebe de recharts x, y, width, height, fill, payload.
const BarraSegmento = ({ x, y, width, height, fill, fillOpacity, payload, topoSe, tracejada }) => {
  if (!(height > 0) || !(width > 0)) return null;
  const r = topoSe && topoSe(payload || {}) ? Math.min(4, width / 2, height) : 0;
  const d = `M${x},${y + height} V${y + r} q0,-${r} ${r},-${r} h${width - 2 * r} q${r},0 ${r},${r} V${y + height} Z`;
  if (tracejada) {
    return <path d={d} fill={fill} fillOpacity={(fillOpacity ?? 1) * 0.45} stroke="#98a2b3" strokeDasharray="3 3" strokeWidth={1} />;
  }
  return <path d={d} fill={fill} fillOpacity={fillOpacity ?? 1} />;
};

// Rótulo no topo da pilha ("291k"); vazio quando o mês não tem dado
const RotuloTopo = ({ x, y, width, value }) => {
  if (!value || !(width > 0)) return null;
  return <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill="#344054">{value}</text>;
};

// Linhas da análise IA: nome, barra proporcional ao maior % da coluna, % e diferença em R$
const LinhasIA = ({ itens, cor, tom, sinal }) => {
  const maior = Math.max(1, ...itens.map((c) => Math.abs(c.pct || 0)));
  return (
    <div>
      {itens.map((c) => (
        <div key={c.codigo} className="po-row">
          <span className="nm" title={`${codCat(c.codigo)} ${c.descricao}`}>
            {codCat(c.codigo) && <small>{codCat(c.codigo)}</small>}{c.descricao}
          </span>
          <span className="tr"><i style={{ width: `${Math.min(100, (Math.abs(c.pct || 0) / maior) * 100)}%`, background: cor }}></i></span>
          <span className={`pc ${tom}`}>
            {sinal}{Math.abs(c.pct || 0).toFixed(0)}%
            <small>{sinal}{formatarValor(Math.abs(c.diferenca || 0))}</small>
          </span>
        </div>
      ))}
    </div>
  );
};

const PlanejamentoCliente = () => {
  const { cliente_id } = useParams();
  const navigate = useNavigate();

  // Estados - Dados
  const [dadosCliente, setDadosCliente] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [dadosAno, setDadosAno] = useState(null);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());

  // Estados - UI
  const [carregando, setCarregando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // Estados - Modal Detalhamento
  const [modalDetalhamento, setModalDetalhamento] = useState(false);
  const [mesDetalhado, setMesDetalhado] = useState(null);
  const [dadosDetalheMes, setDadosDetalheMes] = useState(null);
  const [gruposAbertos, setGruposAbertos] = useState(new Set());
  const [tipoDetalhamento, setTipoDetalhamento] = useState('despesas');

  // Estados - Modal Lançamentos
  const [modalLancamentos, setModalLancamentos] = useState(false);
  const [dadosLancamentos, setDadosLancamentos] = useState(null);
  const [categoriaLancamentos, setCategoriaLancamentos] = useState(null);
  const [carregandoLancamentos, setCarregandoLancamentos] = useState(false);

  // Estados - Modal Histórico Fornecedor
  const [modalHistorico, setModalHistorico] = useState(false);
  const [dadosHistorico, setDadosHistorico] = useState(null);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);

  // Estados - Modal Orçamento
  const [modalOrcamento, setModalOrcamento] = useState(false);
  const [orcamentoTotal, setOrcamentoTotal] = useState('');
  const [distribuicaoOrcamento, setDistribuicaoOrcamento] = useState({});
  const [periodoOrcamento, setPeriodoOrcamento] = useState('ano');
  const [mesSelecionadoOrcamento, setMesSelecionadoOrcamento] = useState(new Date().getMonth() + 1);
  const [salvandoOrcamento, setSalvandoOrcamento] = useState(false);
  const [mesOrcamentos, setMesOrcamentos] = useState({});
  const [valoresMesCarregados, setValoresMesCarregados] = useState({});
  const [orcamentoTotalMes, setOrcamentoTotalMes] = useState('');
  const [orcamentosAtuaisAnual, setOrcamentosAtuaisAnual] = useState({});
  const [totaisAnoAnterior, setTotaisAnoAnterior] = useState({});
  const [totalGeralAnoAnterior, setTotalGeralAnoAnterior] = useState(0);
  const [carregandoAnoAnterior, setCarregandoAnoAnterior] = useState(false);
  // Estados - Modal Orçamento Receitas
  const [modalOrcamentoReceitas, setModalOrcamentoReceitas] = useState(false);
  const [categoriasReceitas, setCategoriasReceitas] = useState([]);
  const [orcamentoTotalReceitas, setOrcamentoTotalReceitas] = useState('');
  const [distribuicaoOrcamentoReceitas, setDistribuicaoOrcamentoReceitas] = useState({});
  const [periodoOrcamentoReceitas, setPeriodoOrcamentoReceitas] = useState('ano');
  const [mesSelecionadoOrcamentoReceitas, setMesSelecionadoOrcamentoReceitas] = useState(new Date().getMonth() + 1);
  const [salvandoOrcamentoReceitas, setSalvandoOrcamentoReceitas] = useState(false);
  const [mesOrcamentosReceitas, setMesOrcamentosReceitas] = useState({});
  const [valoresMesCarregadosReceitas, setValoresMesCarregadosReceitas] = useState({});
  const [orcamentoTotalMesReceitas, setOrcamentoTotalMesReceitas] = useState('');
  const [orcamentosAtuaisAnualReceitas, setOrcamentosAtuaisAnualReceitas] = useState({});
  const [totaisAnoAnteriorReceitas, setTotaisAnoAnteriorReceitas] = useState({});
  const [totalGeralAnoAnteriorReceitas, setTotalGeralAnoAnteriorReceitas] = useState(0);
  const [carregandoAnoAnteriorReceitas, setCarregandoAnoAnteriorReceitas] = useState(false);
  // Estados - Modal Histórico Orçamento
  const [modalHistoricoOrcamento, setModalHistoricoOrcamento] = useState(false);
  const [historicoOrcamento, setHistoricoOrcamento] = useState([]);
  const [carregandoHistoricoOrc, setCarregandoHistoricoOrc] = useState(false);
  // Estados - Modal DRE Completo
  const [modalDRE, setModalDRE] = useState(false);
  const [dadosDRE, setDadosDRE] = useState(null);
  const [carregandoDREYTD, setCarregandoDREYTD] = useState(false);
  const [periodoLabelYTD, setPeriodoLabelYTD] = useState('');
  const [mesInicioDRE, setMesInicioDRE] = useState(1);
  const [mesFimDRE, setMesFimDRE] = useState(new Date().getMonth() + 1);
  const [dreCatsExpandidas, setDreCatsExpandidas] = useState({});
  const [dreLancamentos, setDreLancamentos] = useState({});
  const [dreCarregando, setDreCarregando] = useState({});
  // Estados - Modal Email Fornecedor
  const [modalEmailFornecedor, setModalEmailFornecedor] = useState(false);
  // Destaque da legenda nos gráficos (acende só a série sob o mouse)
  const [destaqueDespesa, setDestaqueDespesa] = useState(null);
  const [destaqueReceita, setDestaqueReceita] = useState(null);
  // Análise automática (IA, sem LLM): categorias que mais ultrapassam/economizam no ano
  const [analiseIA, setAnaliseIA] = useState(null);
  // Modal Orçado x Realizado (todas as categorias, filtro mês a mês)
  const [modalORxR, setModalORxR] = useState(false);
  const [dadosORxR, setDadosORxR] = useState(null);
  const [carregandoORxR, setCarregandoORxR] = useState(false);
  const [mesIniORxR, setMesIniORxR] = useState(1);
  const [mesFimORxR, setMesFimORxR] = useState(new Date().getMonth() + 1);
  const [ordemORxR, setOrdemORxR] = useState({ campo: 'realizado', dir: 'desc' });
  // Modal Gastos não orçados (realizado sem orçamento cadastrado)
  const [modalGNO, setModalGNO] = useState(false);
  const [dadosGNO, setDadosGNO] = useState(null);
  const [carregandoGNO, setCarregandoGNO] = useState(false);
  const [mesIniGNO, setMesIniGNO] = useState(1);
  const [mesFimGNO, setMesFimGNO] = useState(new Date().getMonth() + 1);
  // Modal DRE horizontal (pivot: hierarquia DRE nas linhas, meses nas colunas)
  const [modalDREH, setModalDREH] = useState(false);
  const [dadosDREH, setDadosDREH] = useState(null);
  const [carregandoDREH, setCarregandoDREH] = useState(false);
  const [dreHColapsados, setDreHColapsados] = useState(new Set());
  const [mesIniDREH, setMesIniDREH] = useState(1);
  const [mesFimDREH, setMesFimDREH] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    if (cliente_id) {
      carregarDados(anoSelecionado);
    }
  }, [cliente_id, anoSelecionado]);

  const carregarDados = async (ano) => {
    setCarregando(true);
    const token = localStorage.getItem('access');

    try {
      const resCliente = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/cliente-bpo/${cliente_id}/`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (resCliente.data.success) {
        setDadosCliente(resCliente.data.cliente);
      }

      const resCategorias = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/categorias-orcamento-cliente/`,
        {
          params: { cliente_id: cliente_id },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (resCategorias.data.success) {
        setCategorias(resCategorias.data.categorias);
      }

      const resAno = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/planejamento-ano-cliente/`,
        {
          params: { 
            cliente_id: cliente_id,
            ano: ano
          },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (resAno.data.success) {
        setDadosAno(resAno.data.dados);
      }

      // Análise automática (IA) — não bloqueia o carregamento principal
      try {
        const resIA = await axios.get(
          `${process.env.REACT_APP_API_BASE}/api/analise-orcamento-ytd/`,
          { params: { cliente_id: cliente_id, ano: ano }, headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (resIA.data.success) setAnaliseIA(resIA.data);
      } catch (e) {
        console.error('Erro ao carregar análise IA:', e);
        setAnaliseIA(null);
      }

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      if (error.response?.status === 401) {
        setMensagem('Sessão expirada. Redirecionando...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setMensagem('Erro ao carregar dados');
      }
    } finally {
      setCarregando(false);
    }
  };


  const abrirDetalhamentoMes = async (mesDados, tipo) => {
    const token = localStorage.getItem('access');
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/detalhamento-mes-cliente/`,
        {
          params: {
            cliente_id: cliente_id,
            mes_referencia: mesDados.mes_ano,
            tipo: tipo
          },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setMesDetalhado(mesDados);
        setDadosDetalheMes(response.data.dados);
        setTipoDetalhamento(tipo);
        setModalDetalhamento(true);
      }

    } catch (error) {
      console.error('Erro ao buscar detalhamento:', error);
      setMensagem('Erro ao buscar detalhamento do mês');
      setTimeout(() => setMensagem(''), 3000);
    }
  };

  const abrirHistoricoOrcamento = async () => {
    setModalHistoricoOrcamento(true);
    setCarregandoHistoricoOrc(true);
    try {
      const token = localStorage.getItem('access');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/orcamento-historico/`,
        {
          params: { cliente_id: cliente_id, ano: anoSelecionado },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (res.data.success) setHistoricoOrcamento(res.data.historico);
    } catch (e) {
      console.error('Erro ao carregar histórico de orçamento:', e);
    } finally {
      setCarregandoHistoricoOrc(false);
    }
  };

  const buscarDRE = async (mesIni, mesFim) => {
    setCarregandoDREYTD(true);
    try {
      const token = localStorage.getItem('access');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/dre-completo-ytd/`,
        {
          params: { cliente_id: cliente_id, ano: anoSelecionado, mes_ini: mesIni, mes_fim: mesFim },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (res.data.success) setDadosDRE(res.data);
    } catch (e) {
      console.error('Erro ao carregar DRE:', e);
    } finally {
      setCarregandoDREYTD(false);
    }
  };

  const abrirDREYTD = async () => {
    const mesAtual = new Date().getMonth() + 1;
    setMesInicioDRE(1);
    setMesFimDRE(mesAtual);
    setModalDRE(true);
    await buscarDRE(1, mesAtual);
  };

  const buscarORxR = async (mesIni, mesFim) => {
    setCarregandoORxR(true);
    try {
      const token = localStorage.getItem('access');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/orcamento-realizado-periodo/`,
        { params: { cliente_id, ano: anoSelecionado, mes_ini: mesIni, mes_fim: mesFim }, headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.data.success) setDadosORxR(res.data);
    } catch (e) {
      console.error('Erro ao carregar Orçado x Realizado:', e);
    } finally {
      setCarregandoORxR(false);
    }
  };

  const abrirORxR = async () => {
    const mesAtual = new Date().getMonth() + 1;
    setMesIniORxR(1);
    setMesFimORxR(mesAtual);
    setOrdemORxR({ campo: 'realizado', dir: 'desc' });
    setModalORxR(true);
    await buscarORxR(1, mesAtual);
  };

  // Ordena a tabela Orçado x Realizado; clicar de novo na mesma coluna inverte (menor↔maior)
  const ordenarORxR = (campo) => {
    setOrdemORxR(prev => prev.campo === campo
      ? { campo, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { campo, dir: campo === 'codigo' || campo === 'descricao' ? 'asc' : 'desc' });
  };

  // Gastos não orçados: reaproveita o mesmo endpoint e filtra orçado = 0
  const buscarGNO = async (mesIni, mesFim) => {
    setCarregandoGNO(true);
    try {
      const token = localStorage.getItem('access');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/orcamento-realizado-periodo/`,
        { params: { cliente_id, ano: anoSelecionado, mes_ini: mesIni, mes_fim: mesFim }, headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.data.success) setDadosGNO(res.data);
    } catch (e) {
      console.error('Erro ao carregar gastos não orçados:', e);
    } finally {
      setCarregandoGNO(false);
    }
  };

  const abrirGNO = async () => {
    const mesAtual = new Date().getMonth() + 1;
    setMesIniGNO(1);
    setMesFimGNO(mesAtual);
    setModalGNO(true);
    await buscarGNO(1, mesAtual);
  };

  // DRE horizontal (pivot estilo Omie)
  const buscarDREH = async (mesIni, mesFim) => {
    setCarregandoDREH(true);
    try {
      const token = localStorage.getItem('access');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/dre-horizontal/`,
        { params: { cliente_id, ano: anoSelecionado, mes_ini: mesIni, mes_fim: mesFim }, headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.data.success) setDadosDREH(res.data);
      else setDadosDREH({ erro: res.data.erro || 'Falha ao gerar DRE.' });
    } catch (e) {
      console.error('Erro ao carregar DRE horizontal:', e);
      setDadosDREH({ erro: e.response?.data?.erro || 'Erro ao carregar DRE horizontal.' });
    } finally {
      setCarregandoDREH(false);
    }
  };

  const abrirDREH = async () => {
    const mesAtual = new Date().getMonth() + 1;
    setMesIniDREH(1);
    setMesFimDREH(mesAtual);
    setModalDREH(true);
    setDadosDREH(null);
    setDreHColapsados(new Set());
    await buscarDREH(1, mesAtual);
  };

  const toggleDREH = (codigo) => {
    setDreHColapsados(prev => {
      const next = new Set(prev);
      next.has(codigo) ? next.delete(codigo) : next.add(codigo);
      return next;
    });
  };

  // Expande/colapsa os lançamentos de uma categoria (folha) do DRE horizontal, coerente
  // com a base do DRE (competência por vencimento + extrato). Reaproveita os buckets do DRE.
  const toggleLancamentosDREH = async (codigo) => {
    const key = `dreh_${codigo}`;
    if (dreCatsExpandidas[key]) {
      setDreCatsExpandidas(prev => ({ ...prev, [key]: false }));
      return;
    }
    setDreCatsExpandidas(prev => ({ ...prev, [key]: true }));
    if (dreLancamentos[key]) return;
    setDreCarregando(prev => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem('access');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/dre-horizontal-lancamentos/`,
        { params: { cliente_id, ano: anoSelecionado, mes_ini: mesIniDREH, mes_fim: mesFimDREH, codigo_categoria: codigo },
          headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.data.success) setDreLancamentos(prev => ({ ...prev, [key]: res.data.lancamentos || [] }));
    } catch (e) {
      console.error('Erro ao buscar lançamentos do DRE horizontal:', e);
    } finally {
      setDreCarregando(prev => ({ ...prev, [key]: false }));
    }
  };

  const abrirModalOrcamento = async () => {
    setDistribuicaoOrcamento({});
    setMesOrcamentos({});
    setValoresMesCarregados({});
    setOrcamentosAtuaisAnual({});
    setOrcamentoTotal('');
    setOrcamentoTotalMes('');
    setPeriodoOrcamento('ano');
    setMesSelecionadoOrcamento(new Date().getMonth() + 1);
    setTotaisAnoAnterior({});
    setTotalGeralAnoAnterior(0);
    setModalOrcamento(true);

    const token = localStorage.getItem('access');

    // Carregar orçamentos anuais por categoria (soma dos 12 meses de orcamento_mensal)
    try {
      const resAnual = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/orcamento-anual-por-categoria/`,
        {
          params: { cliente_id: cliente_id, ano: anoSelecionado },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (resAnual.data.success) {
        setOrcamentosAtuaisAnual(resAnual.data.valores);
      }
    } catch (e) {
      console.error('Erro ao carregar orçamentos anuais por categoria:', e);
    }

    // Carregar orçamento anual já salvo para o ano atual
    try {
      const resSalvo = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/orcamento-anual/`,
        {
          params: { cliente_id: cliente_id, ano: anoSelecionado },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (resSalvo.data.success && resSalvo.data.valor > 0) {
        setOrcamentoTotal(
          new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(resSalvo.data.valor)
        );
      }
    } catch (e) {
      console.error('Erro ao carregar orçamento anual salvo:', e);
    }

    // Buscar totais do ano anterior como referência
    setCarregandoAnoAnterior(true);
    try {
      const anoAnt = anoSelecionado - 1;
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/totais-ano-por-categoria/`,
        {
          params: { cliente_id: cliente_id, ano: anoAnt },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (res.data.success) {
        const map = {};
        res.data.totais.forEach(t => { map[t.codigo] = t.total; });
        setTotaisAnoAnterior(map);
        setTotalGeralAnoAnterior(res.data.total_geral);
      }
    } catch (e) {
      console.error('Erro ao buscar totais ano anterior:', e);
    } finally {
      setCarregandoAnoAnterior(false);
    }
  };

  const MESES_NOMES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

  const formatarValoresMes = (valoresMap) => {
    const result = {};
    Object.entries(valoresMap).forEach(([codigo, valor]) => {
      if (valor > 0) {
        result[codigo] = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor);
      }
    });
    return result;
  };

  const carregarValoresMes = async (mesAno, mesOrcamentosAtual) => {
    const calcMetaMensal = (valores) => {
      const soma = Object.values(valores).reduce((s, v) => {
        return s + (parseFloat((v || '').replace(/\./g, '').replace(',', '.')) || 0);
      }, 0);
      if (soma > 0) {
        return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(soma);
      }
      const anualNum = parseFloat((orcamentoTotal || '').replace(/\./g, '').replace(',', '.')) || 0;
      if (anualNum > 0) {
        return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(anualNum / 12);
      }
      return '';
    };

    if (mesOrcamentosAtual[mesAno] !== undefined) {
      const cached = mesOrcamentosAtual[mesAno];
      setValoresMesCarregados(cached);
      setDistribuicaoOrcamento({ ...cached });
      setOrcamentoTotalMes(calcMetaMensal(cached));
      return mesOrcamentosAtual;
    }
    try {
      const token = localStorage.getItem('access');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/orcamento-mensal-categorias/`,
        {
          params: { cliente_id: cliente_id, mes_referencia: mesAno },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const valores = (res.data.success && Object.keys(res.data.valores).length > 0)
        ? formatarValoresMes(res.data.valores)
        : {};
      const updated = { ...mesOrcamentosAtual, [mesAno]: valores };
      setMesOrcamentos(updated);
      setValoresMesCarregados(valores);
      setDistribuicaoOrcamento({ ...valores });
      setOrcamentoTotalMes(calcMetaMensal(valores));
      return updated;
    } catch (e) {
      console.error('Erro ao carregar valores do mês:', e);
      const updated = { ...mesOrcamentosAtual, [mesAno]: {} };
      setMesOrcamentos(updated);
      setValoresMesCarregados({});
      setDistribuicaoOrcamento({});
      setOrcamentoTotalMes('');
      return updated;
    }
  };

  const navegarMes = async (direcao) => {
    const mesAtualAno = `${anoSelecionado}-${String(mesSelecionadoOrcamento).padStart(2, '0')}`;
    // Salva estado atual no cache antes de navegar
    const updated = { ...mesOrcamentos, [mesAtualAno]: distribuicaoOrcamento };
    setMesOrcamentos(updated);

    const novoMes = mesSelecionadoOrcamento + direcao;
    const mesFinal = novoMes < 1 ? 12 : novoMes > 12 ? 1 : novoMes;
    const novoMesAno = `${anoSelecionado}-${String(mesFinal).padStart(2, '0')}`;

    setMesSelecionadoOrcamento(mesFinal);
    await carregarValoresMes(novoMesAno, updated);
  };

  const salvarTodosOrcamentos = async () => {
    setSalvandoOrcamento(true);
    const token = localStorage.getItem('access');

    try {
      // Monta UMA lista de itens e envia em um único request (evita centenas de conexões)
      const parseBR = (v) => parseFloat(String(v).replace(/\./g, '').replace(',', '.')) || 0;
      const itens = [];

      if (periodoOrcamento === 'mes') {
        const mesAtualAno = `${anoSelecionado}-${String(mesSelecionadoOrcamento).padStart(2, '0')}`;
        const todosMeses = { ...mesOrcamentos, [mesAtualAno]: distribuicaoOrcamento };
        for (const [mesRef, valores] of Object.entries(todosMeses)) {
          Object.entries(valores).filter(([_, v]) => v).forEach(([codigo, valor]) => {
            itens.push({ codigo_categoria: codigo, mes_referencia: mesRef, orcamento: parseBR(valor) });
          });
        }
      } else {
        // Modo 'ano': valor inserido é anual → divide por 12 e replica nos 12 meses
        Object.entries(distribuicaoOrcamento).filter(([_, v]) => v).forEach(([codigo, valor]) => {
          const valorMensal = Math.round((parseBR(valor) / 12) * 100) / 100;
          for (let mes = 1; mes <= 12; mes++) {
            const mesRef = `${anoSelecionado}-${String(mes).padStart(2, '0')}`;
            itens.push({ codigo_categoria: codigo, mes_referencia: mesRef, orcamento: valorMensal });
          }
        });
      }

      if (itens.length > 0) {
        await axios.post(
          `${process.env.REACT_APP_API_BASE}/api/atualizar-orcamento-lote/`,
          { cliente_id, ano: anoSelecionado, tipo: 'despesas', periodo: periodoOrcamento, itens },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }

      // Salvar o total anual se preenchido e período = ano
      const totalNum = parseFloat((orcamentoTotal || '').replace(/\./g, '').replace(',', '.')) || 0;
      if (totalNum > 0 && periodoOrcamento === 'ano') {
        await axios.post(
          `${process.env.REACT_APP_API_BASE}/api/orcamento-anual/`,
          { cliente_id: cliente_id, ano: anoSelecionado, valor: totalNum },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }

      setMensagem('Orçamentos cadastrados com sucesso!');
      setModalOrcamento(false);
      carregarDados(anoSelecionado);
      setTimeout(() => setMensagem(''), 3000);
    } catch (error) {
      console.error('Erro ao salvar orçamentos:', error);
      setMensagem('Erro ao salvar orçamentos');
      setTimeout(() => setMensagem(''), 3000);
    } finally {
      setSalvandoOrcamento(false);
    }
  };

  const abrirModalOrcamentoReceitas = async () => {
    setDistribuicaoOrcamentoReceitas({});
    setMesOrcamentosReceitas({});
    setValoresMesCarregadosReceitas({});
    setOrcamentosAtuaisAnualReceitas({});
    setOrcamentoTotalReceitas('');
    setOrcamentoTotalMesReceitas('');
    setPeriodoOrcamentoReceitas('ano');
    setMesSelecionadoOrcamentoReceitas(new Date().getMonth() + 1);
    setTotaisAnoAnteriorReceitas({});
    setTotalGeralAnoAnteriorReceitas(0);
    setModalOrcamentoReceitas(true);

    const token = localStorage.getItem('access');

    try {
      const resCats = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/categorias-receitas-cliente/`,
        { params: { cliente_id }, headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (resCats.data.success) setCategoriasReceitas(resCats.data.categorias);
    } catch (e) { console.error('Erro ao carregar categorias receitas:', e); }

    try {
      const resAnual = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/orcamento-anual-por-categoria/`,
        { params: { cliente_id, ano: anoSelecionado, tipo: 'receitas' }, headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (resAnual.data.success) setOrcamentosAtuaisAnualReceitas(resAnual.data.valores);
    } catch (e) { console.error('Erro ao carregar orçamentos anuais receitas:', e); }

    try {
      const resSalvo = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/orcamento-anual-receitas/`,
        { params: { cliente_id, ano: anoSelecionado }, headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (resSalvo.data.success && resSalvo.data.valor > 0) {
        setOrcamentoTotalReceitas(
          new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(resSalvo.data.valor)
        );
      }
    } catch (e) { console.error('Erro ao carregar meta anual receitas:', e); }

    setCarregandoAnoAnteriorReceitas(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/totais-ano-receitas-por-categoria/`,
        { params: { cliente_id, ano: anoSelecionado - 1 }, headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.data.success) {
        const map = {};
        res.data.totais.forEach(t => { map[t.codigo] = t.total; });
        setTotaisAnoAnteriorReceitas(map);
        setTotalGeralAnoAnteriorReceitas(res.data.total_geral);
      }
    } catch (e) { console.error('Erro ao buscar totais receitas ano anterior:', e); }
    finally { setCarregandoAnoAnteriorReceitas(false); }
  };

  const carregarValoresMesReceitas = async (mesAno, mesOrcAtual) => {
    if (mesOrcAtual[mesAno] !== undefined) {
      const cached = mesOrcAtual[mesAno];
      setValoresMesCarregadosReceitas({ ...cached });
      setDistribuicaoOrcamentoReceitas({ ...cached });
      return mesOrcAtual;
    }
    const token = localStorage.getItem('access');
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/orcamento-mensal-categorias/`,
        { params: { cliente_id, mes_referencia: mesAno, tipo: 'receitas' }, headers: { 'Authorization': `Bearer ${token}` } }
      );
      const valores = res.data.success ? formatarValoresMes(res.data.valores || {}) : {};
      const updated = { ...mesOrcAtual, [mesAno]: valores };
      setMesOrcamentosReceitas(updated);
      setValoresMesCarregadosReceitas({ ...valores });
      setDistribuicaoOrcamentoReceitas({ ...valores });
      const soma = Object.values(valores).reduce((s, v) => s + (parseFloat((v || '').replace(/\./g, '').replace(',', '.')) || 0), 0);
      setOrcamentoTotalMesReceitas(soma > 0 ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(soma) : '');
      return updated;
    } catch (e) {
      const updated = { ...mesOrcAtual, [mesAno]: {} };
      setMesOrcamentosReceitas(updated);
      setValoresMesCarregadosReceitas({});
      setDistribuicaoOrcamentoReceitas({});
      setOrcamentoTotalMesReceitas('');
      return updated;
    }
  };

  const navegarMesReceitas = async (direcao) => {
    const mesAtualAno = `${anoSelecionado}-${String(mesSelecionadoOrcamentoReceitas).padStart(2, '0')}`;
    const updated = { ...mesOrcamentosReceitas, [mesAtualAno]: distribuicaoOrcamentoReceitas };
    setMesOrcamentosReceitas(updated);
    const novoMes = mesSelecionadoOrcamentoReceitas + direcao;
    const mesFinal = novoMes < 1 ? 12 : novoMes > 12 ? 1 : novoMes;
    const novoMesAno = `${anoSelecionado}-${String(mesFinal).padStart(2, '0')}`;
    setMesSelecionadoOrcamentoReceitas(mesFinal);
    await carregarValoresMesReceitas(novoMesAno, updated);
  };

  const salvarTodosOrcamentosReceitas = async () => {
    setSalvandoOrcamentoReceitas(true);
    const token = localStorage.getItem('access');
    try {
      const parseBR = (v) => parseFloat(String(v).replace(/\./g, '').replace(',', '.')) || 0;
      const itens = [];
      if (periodoOrcamentoReceitas === 'mes') {
        const mesAtualAno = `${anoSelecionado}-${String(mesSelecionadoOrcamentoReceitas).padStart(2, '0')}`;
        const todosMeses = { ...mesOrcamentosReceitas, [mesAtualAno]: distribuicaoOrcamentoReceitas };
        for (const [mesRef, valores] of Object.entries(todosMeses)) {
          Object.entries(valores).filter(([_, v]) => v).forEach(([codigo, valor]) => {
            itens.push({ codigo_categoria: codigo, mes_referencia: mesRef, orcamento: parseBR(valor) });
          });
        }
      } else {
        Object.entries(distribuicaoOrcamentoReceitas).filter(([_, v]) => v).forEach(([codigo, valor]) => {
          const valorMensal = Math.round((parseBR(valor) / 12) * 100) / 100;
          for (let mes = 1; mes <= 12; mes++) {
            const mesRef = `${anoSelecionado}-${String(mes).padStart(2, '0')}`;
            itens.push({ codigo_categoria: codigo, mes_referencia: mesRef, orcamento: valorMensal });
          }
        });
      }

      if (itens.length > 0) {
        await axios.post(
          `${process.env.REACT_APP_API_BASE}/api/atualizar-orcamento-lote/`,
          { cliente_id, ano: anoSelecionado, tipo: 'receitas', periodo: periodoOrcamentoReceitas, itens },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }

      // Salvar total anual se período = ano e valor preenchido
      const totalRecNum = parseFloat((orcamentoTotalReceitas || '').replace(/\./g, '').replace(',', '.')) || 0;
      if (totalRecNum > 0 && periodoOrcamentoReceitas === 'ano') {
        await axios.post(
          `${process.env.REACT_APP_API_BASE}/api/orcamento-anual-receitas/`,
          { cliente_id, ano: anoSelecionado, valor: totalRecNum },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }

      setMensagem('Orçamento de receitas cadastrado com sucesso!');
      setModalOrcamentoReceitas(false);
      carregarDados(anoSelecionado);
      setTimeout(() => setMensagem(''), 3000);
    } catch (error) {
      setMensagem('Erro ao salvar orçamento de receitas');
      setTimeout(() => setMensagem(''), 3000);
    } finally {
      setSalvandoOrcamentoReceitas(false);
    }
  };

  const toggleLancamentosDRE = async (cat, tipo) => {
    const key = `${tipo}_${cat.codigo}`;
    // Colapsar se já aberto
    if (dreCatsExpandidas[key]) {
      setDreCatsExpandidas(prev => ({ ...prev, [key]: false }));
      return;
    }
    // Expandir — buscar se ainda não carregado
    setDreCatsExpandidas(prev => ({ ...prev, [key]: true }));
    if (dreLancamentos[key]) return;

    setDreCarregando(prev => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem('access');
      const ano = anoSelecionado;
      const dataIni = `${ano}-${String(mesInicioDRE).padStart(2, '0')}-01`;
      const ultimoDia = new Date(ano, mesFimDRE, 0).getDate();
      const dataFim = `${ano}-${String(mesFimDRE).padStart(2, '0')}-${ultimoDia}`;
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/lancamentos-categoria-mes/`,
        {
          params: { cliente_id, data_ini: dataIni, data_fim: dataFim, codigo_categoria: cat.codigo, tipo },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.data.success) {
        setDreLancamentos(prev => ({ ...prev, [key]: response.data.dados.lancamentos || [] }));
      }
    } catch (e) {
      console.error('Erro ao buscar lançamentos DRE:', e);
    } finally {
      setDreCarregando(prev => ({ ...prev, [key]: false }));
    }
  };

  const abrirLancamentosCategoria = async (cat) => {
    const token = localStorage.getItem('access');
    setCarregandoLancamentos(true);
    setCategoriaLancamentos(cat);
    setModalLancamentos(true);
    setDadosLancamentos(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/lancamentos-categoria-mes/`,
        {
          params: {
            cliente_id: cliente_id,
            mes_referencia: mesDetalhado.mes_ano,
            codigo_categoria: cat.codigo,
            tipo: tipoDetalhamento
          },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.data.success) {
        setDadosLancamentos(response.data.dados);
      }
    } catch (error) {
      console.error('Erro ao buscar lançamentos:', error);
    } finally {
      setCarregandoLancamentos(false);
    }
  };

  // Expande/colapsa os lançamentos de uma categoria INLINE (para baixo, igual ao DRE),
  // considerando um intervalo de meses. Usado nos modais "Orçado x Realizado" e
  // "Gastos não orçados". Reaproveita os buckets dreCatsExpandidas/dreLancamentos/dreCarregando
  // com prefixo de chave próprio (ex.: orxr_, gno_).
  const toggleLancamentosInline = async (cat, mesIni, mesFim, prefix) => {
    const key = `${prefix}_${cat.codigo}`;
    if (dreCatsExpandidas[key]) {
      setDreCatsExpandidas(prev => ({ ...prev, [key]: false }));
      return;
    }
    setDreCatsExpandidas(prev => ({ ...prev, [key]: true }));
    if (dreLancamentos[key]) return;

    setDreCarregando(prev => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem('access');
      const ano = anoSelecionado;
      const dataIni = `${ano}-${String(mesIni).padStart(2, '0')}-01`;
      const ultimoDia = new Date(ano, mesFim, 0).getDate();
      const dataFim = `${ano}-${String(mesFim).padStart(2, '0')}-${ultimoDia}`;
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/lancamentos-categoria-mes/`,
        {
          params: { cliente_id, data_ini: dataIni, data_fim: dataFim, codigo_categoria: cat.codigo, tipo: 'despesas' },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.data.success) {
        setDreLancamentos(prev => ({ ...prev, [key]: response.data.dados.lancamentos || [] }));
      }
    } catch (e) {
      console.error('Erro ao buscar lançamentos:', e);
    } finally {
      setDreCarregando(prev => ({ ...prev, [key]: false }));
    }
  };

  const abrirHistoricoFornecedor = async (lanc) => {
    if (!lanc.codigo_fornecedor) return;
    const token = localStorage.getItem('access');
    setCarregandoHistorico(true);
    setDadosHistorico(null);
    setModalHistorico(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/historico-fornecedor/`,
        {
          params: { cliente_id: cliente_id, codigo_fornecedor: lanc.codigo_fornecedor },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.data.success) {
        setDadosHistorico(response.data.dados);
      }
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
    } finally {
      setCarregandoHistorico(false);
    }
  };

  const CustomTooltipDespesas = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const row = (payload[0] && payload[0].payload) || {};
      const realizado = row.despesas_realizadas || 0;
      const orcado = row.orcamento_total || 0;
      const ultrapassado = orcado > 0 ? Math.max(0, realizado - orcado) : 0;
      const pct = orcado > 0 ? (realizado / orcado) * 100 : 0;
      const corBar = pct > 100 ? '#c2410c' : pct >= 80 ? '#b45309' : '#0b1f4b';
      return (
        <TooltipCard titulo={`${label} · Despesas`} acento="#b71c1c">
          {orcado > 0 && <TooltipLinha rest rotulo="Orçado" valor={orcado} />}
          <TooltipLinha cor="#0b1f4b" rotulo="Realizado" valor={realizado} />
          {ultrapassado > 0 && <TooltipLinha cor="#c2410c" rotulo="Acima do orçado" valor={ultrapassado} destaque />}
          {orcado > 0 && <TooltipBarra rotulo="Execução do orçamento" pct={pct} cor={corBar} />}
        </TooltipCard>
      );
    }
    return null;
  };

  const CustomTooltipReceitas = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const row = (payload[0] && payload[0].payload) || {};
      const realizado = row.receitas_realizadas || 0;
      const orcado = row.orcamento_receitas || 0;
      const atingido = orcado > 0 ? Math.min(realizado, orcado) : realizado;
      const acima = orcado > 0 ? Math.max(0, realizado - orcado) : 0;
      const pct = orcado > 0 ? (realizado / orcado) * 100 : 0;
      const corBar = pct >= 100 ? '#15803d' : pct >= 70 ? '#0e7490' : '#b45309';
      return (
        <TooltipCard titulo={`${label} · Receitas`} acento="#1b5e20">
          {orcado > 0 && <TooltipLinha rest rotulo="Meta" valor={orcado} />}
          <TooltipLinha cor="#0e7490" rotulo="Recebido" valor={atingido} />
          {acima > 0 && <TooltipLinha cor="#15803d" rotulo="Acima da meta" valor={acima} destaque />}
          {orcado > 0 && <TooltipBarra rotulo="Atingimento da meta" pct={pct} cor={corBar} />}
        </TooltipCard>
      );
    }
    return null;
  };

  if (carregando && !dadosCliente) {
    return (
      <div className="das-page">
        <div className="container-fluid mt-5">
          <div className="text-center">
            <div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}}>
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="mt-3 text-muted">Carregando Planejamento Orçamentário...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!dadosCliente) {
    return (
      <div className="das-page">
        <div className="container-fluid mt-5">
          <div className="alert alert-danger text-center">
            <h4>Cliente não encontrado</h4>
            <button className="btn btn-primary mt-3" onClick={() => navigate('/Clientes_bpo')}>
              Voltar para Lista de Clientes
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Opacidade de cada série conforme o destaque da legenda (acende só a série sob o mouse)
  const op = (key, destaque) => (!destaque || destaque === key ? 1 : 0.15);

  // Segmentos empilhados das DESPESAS (leitura por intensidade — a barra é o orçamento):
  //  - Realizado (vermelho médio): parte do orçamento já consumida (preenchido)
  //  - Não realizado (cinza fraco): orçamento ainda não usado (vazio)
  //  - Ultrapassado (vermelho forte): o que passou do orçado (transbordo)
  const dadosDespesas = (dadosAno?.meses_completos || []).map((m) => {
    const orcado = m.orcamento_total || 0;
    const real = m.despesas_realizadas || 0;
    const temOrc = orcado > 0;
    return {
      ...m,
      desp_realizado: temOrc ? Math.min(real, orcado) : real,
      desp_ultrapassado: temOrc ? Math.max(0, real - orcado) : 0,
      desp_nao_realizado: temOrc ? Math.max(0, orcado - real) : 0,
      desp_label: real > 0 ? rotuloK(real) : '',
    };
  });

  // Segmentos empilhados das RECEITAS: atingido + acima da meta + meta restante
  const dadosReceitas = (dadosAno?.meses_completos || []).map((m) => {
    const meta = m.orcamento_receitas || 0;
    const real = m.receitas_realizadas || 0;
    return {
      ...m,
      rec_atingido: meta > 0 ? Math.min(real, meta) : real,
      rec_acima: meta > 0 ? Math.max(0, real - meta) : 0,
      rec_meta_rest: meta > 0 ? Math.max(0, meta - real) : 0,
      rec_label: real > 0 ? rotuloK(real) : '',
    };
  });

  const somar = (arr, key) => arr.reduce((s, m) => s + (m[key] || 0), 0);
  const seriesDespesas = [
    { key: 'desp_realizado', nome: 'Dentro do orçado', cor: '#0b1f4b', total: somar(dadosDespesas, 'desp_realizado') },
    { key: 'desp_ultrapassado', nome: 'Acima do orçado', cor: '#c2410c', total: somar(dadosDespesas, 'desp_ultrapassado') },
    { key: 'desp_nao_realizado', nome: 'Orçado não realizado', cor: '#e6e9ef', rest: true, total: somar(dadosDespesas, 'desp_nao_realizado') },
  ];
  const seriesReceitas = [
    { key: 'rec_atingido', nome: 'Meta recebida', cor: '#0e7490', total: somar(dadosReceitas, 'rec_atingido') },
    { key: 'rec_acima', nome: 'Acima da meta', cor: '#15803d', total: somar(dadosReceitas, 'rec_acima') },
    { key: 'rec_meta_rest', nome: 'Meta não recebida', cor: '#e6e9ef', rest: true, total: somar(dadosReceitas, 'rec_meta_rest') },
  ];

  // ── Redesign (23/10/2026): KPIs, pílulas dos gráficos e linha do orçado mensal ──
  const resumo = dadosAno?.resumo || {};
  const mesesCompletos = dadosAno?.meses_completos || [];
  const nMeses = resumo.meses_com_dados || 0;
  const totOrcado = resumo.total_orcado || 0;
  const totDesp = resumo.total_despesas || 0;
  const totRec = resumo.total_receitas || 0;
  const metaRec = mesesCompletos.reduce((s, m) => s + (m.orcamento_receitas || 0), 0);
  // orçado dos meses que já têm dado (para comparar com o realizado sem distorcer pelo ano inteiro)
  const orcadoAteAgora = mesesCompletos.slice(0, nMeses).reduce((s, m) => s + (m.orcamento_total || 0), 0);
  const resultadoAno = totRec - totDesp;
  const pctExec = totOrcado > 0 ? (totDesp / totOrcado) * 100 : null;
  const esperadoPct = (nMeses / 12) * 100;
  const deltaOrcado = orcadoAteAgora > 0 ? (totDesp / orcadoAteAgora - 1) * 100 : null;
  const pctMeta = metaRec > 0 ? (totRec / metaRec) * 100 : null;
  const margem = totRec > 0 ? (resultadoAno / totRec) * 100 : null;
  const pct1 = (v) => `${v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  const pctSinal = (v) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${pct1(Math.abs(v))}`;
  const fmtInteiro = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v || 0);
  // média mensal do orçado/meta (linha de referência dos gráficos); some quando não há orçamento
  const mediaDe = (key) => {
    const com = mesesCompletos.filter((m) => (m[key] || 0) > 0);
    return com.length ? com.reduce((s, m) => s + m[key], 0) / com.length : 0;
  };
  const orcadoMes = mediaDe('orcamento_total');
  const metaMes = mediaDe('orcamento_receitas');
  const mesesAcimaOrc = dadosDespesas.filter((m) => m.desp_ultrapassado > 0).length;
  const mesesAcimaMeta = dadosReceitas.filter((m) => m.rec_acima > 0).length;
  const mesesComMeta = dadosReceitas.filter((m) => (m.orcamento_receitas || 0) > 0).length;
  const anosOpcoes = [2024, 2025, 2026, 2027];

  return (
    <div className="das-page fmp-brand-page po-page">
      <div className="po-wrap">

        {/* HEADER */}
        <header className="po-hd">
          <div>
            <div className="po-eyebrow">Financial Management Platform · Planejamento orçamentário</div>
            <h1>{dadosCliente.nome}</h1>
            <div className="po-meta">
              <span className="po-chip">CNPJ <b>{dadosCliente.cnpj}</b></span>
              {dadosCliente.regime_tributario && <span className="po-chip">Regime <b>{dadosCliente.regime_tributario}</b></span>}
              {dadosCliente.nome_operador && <span className="po-chip">Operador <b>{dadosCliente.nome_operador}</b></span>}
              <span className="po-chip">Ano de referência <b>{anoSelecionado}</b></span>
            </div>
          </div>
          <div className="po-hd-right">
            <button type="button" className="po-link" onClick={() => navigate(`/cliente/${cliente_id}/dashboard`)} title="Voltar ao Dashboard">
              <i className="fas fa-arrow-left"></i>Voltar ao Dashboard
            </button>
            <div className="po-seg" role="group" aria-label="Ano">
              {anosOpcoes.map((ano) => (
                <button key={ano} type="button" className={`po-tab ${ano === anoSelecionado ? 'on' : ''}`}
                  aria-pressed={ano === anoSelecionado} onClick={() => setAnoSelecionado(ano)}>{ano}</button>
              ))}
            </div>
            <div className="po-acts">
              <button type="button" className="po-btn ghost" onClick={abrirHistoricoOrcamento} title="Histórico de alterações de orçamento">
                <i className="fas fa-history"></i>Histórico
              </button>
              <button type="button" className="po-btn" onClick={abrirDREYTD} disabled={carregandoDREYTD || !categorias.length}
                title="DRE acumulado do ano até o mês atual">
                {carregandoDREYTD ? <span className="po-spin"></span> : <i className="fas fa-chart-bar"></i>}DRE {anoSelecionado}
              </button>
              <button type="button" className="po-btn" onClick={() => navigate(`/Dre_horizontal/${cliente_id}`)} disabled={!categorias.length}
                title="DRE horizontal: hierarquia nas linhas e meses nas colunas">
                <i className="fas fa-table-cells"></i>DRE horizontal
              </button>
              <button type="button" className="po-btn" onClick={abrirORxR} disabled={carregandoORxR || !categorias.length}
                title="Orçado x Realizado de todas as categorias, com filtro de meses">
                {carregandoORxR ? <span className="po-spin"></span> : <i className="fas fa-scale-balanced"></i>}Orçado × Realizado
              </button>
              <button type="button" className="po-btn" onClick={abrirGNO} disabled={carregandoGNO || !categorias.length}
                title="Gastos realizados sem orçamento cadastrado">
                {carregandoGNO ? <span className="po-spin"></span> : <i className="fas fa-triangle-exclamation"></i>}Gastos não orçados
              </button>
              <button type="button" className="po-btn primary" onClick={() => navigate(`/Orcamento_despesas/${cliente_id}?ano=${anoSelecionado}`)}
                disabled={!categorias.length}>
                <i className="fas fa-sliders-h"></i>Cadastrar orçamento
              </button>
            </div>
          </div>
        </header>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {/* KPIs DO ANO */}
        {dadosAno && (
          <section className="po-kpis">
            <div className="po-kpi">
              <div className="po-eyebrow">Orçado · despesas</div>
              <div className="v po-num">{fmtInteiro(totOrcado)}</div>
              <div className="po-bar"><i style={{ width: `${Math.min(100, pctExec || 0)}%`, background: 'var(--po-bar-within)' }}></i></div>
              <div className="sub">
                {pctExec === null
                  ? <span>sem orçamento cadastrado</span>
                  : <>
                      <span>realizado <b>{pct1(pctExec)}</b> em {nMeses} {nMeses === 1 ? 'mês' : 'meses'}</span>
                      <span className={pctExec > esperadoPct + 5 ? 'po-bad' : 'po-good'}>{pctExec > esperadoPct + 5 ? 'acima do ritmo' : 'no ritmo'}</span>
                    </>}
              </div>
            </div>
            <div className="po-kpi">
              <div className="po-eyebrow">Saídas realizadas</div>
              <div className="v po-num">{fmtInteiro(totDesp)}</div>
              <div className="po-bar"><i style={{ width: `${Math.min(100, pctExec || 0)}%`, background: 'var(--po-bar-within)' }}></i></div>
              <div className="sub">
                <span>média <b>{fmtInteiro(nMeses > 0 ? totDesp / nMeses : 0)}</b>/mês</span>
                {deltaOrcado === null
                  ? <span>sem orçamento cadastrado</span>
                  : <span className={`po-pill ${deltaOrcado > 0 ? 'bad' : 'good'}`}>{pctSinal(deltaOrcado)} vs orçado</span>}
              </div>
            </div>
            <div className="po-kpi">
              <div className="po-eyebrow">Entradas realizadas</div>
              <div className="v po-num">{fmtInteiro(totRec)}</div>
              <div className="po-bar"><i style={{ width: `${Math.min(100, pctMeta || 0)}%`, background: 'var(--po-bar-rec)' }}></i></div>
              <div className="sub">
                {pctMeta === null
                  ? <span>sem meta de receitas cadastrada</span>
                  : <>
                      <span>meta <b>{fmtInteiro(metaRec)}</b></span>
                      <span className={`po-pill ${pctMeta >= 100 ? 'good' : 'warn'}`}>{pct1(pctMeta)} da meta</span>
                    </>}
              </div>
            </div>
            <div className="po-kpi">
              <div className="po-eyebrow">Resultado do ano</div>
              <div className={`v po-num ${resultadoAno >= 0 ? 'po-good' : 'po-bad'}`}>{resultadoAno < 0 ? '−' : ''}{fmtInteiro(Math.abs(resultadoAno))}</div>
              <div className="po-bar"><i style={{ width: `${Math.min(100, Math.max(0, margem || 0))}%`, background: resultadoAno >= 0 ? 'var(--po-bar-above)' : 'var(--po-red)' }}></i></div>
              <div className="sub">
                <span>{margem === null ? 'sem receitas no período' : <>margem <b>{pct1(margem)}</b></>}</span>
                <span>{resultadoAno >= 0 ? 'entradas > saídas' : 'saídas > entradas'}</span>
              </div>
            </div>
          </section>
        )}

        {/* GRÁFICOS: DESPESAS E RECEITAS LADO A LADO */}
        {dadosAno && mesesCompletos.length > 0 && (
          <section className="po-charts">
            <div className="po-card">
              <div className="po-card-h">
                <div><h2>Despesas {anoSelecionado}</h2><div className="s">Orçado × realizado por mês · clique no mês para o detalhe</div></div>
                {orcadoMes > 0
                  ? (mesesAcimaOrc > 0
                    ? <span className="po-pill bad">{mesesAcimaOrc} {mesesAcimaOrc === 1 ? 'mês' : 'meses'} acima do orçado</span>
                    : <span className="po-pill good">dentro do orçado</span>)
                  : <span className="po-pill neutral">sem orçamento</span>}
              </div>
              <div className="po-chart">
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={dadosDespesas} barCategoryGap="28%" margin={{ top: 22, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="var(--po-line)" />
                    <XAxis
                      dataKey="mes"
                      axisLine={false}
                      tickLine={false}
                      tick={(props) => {
                        const { x, y, payload } = props;
                        const mesData = dadosAno.meses_completos.find(m => m.mes === payload.value);
                        return (
                          <g transform={`translate(${x},${y})`} onClick={() => mesData && abrirDetalhamentoMes(mesData, 'despesas')} style={{ cursor: 'pointer' }}>
                            <text x={0} y={0} dy={14} textAnchor="middle" fill="#344054" fontSize={12} fontWeight={600}>{payload.value}</text>
                          </g>
                        );
                      }}
                    />
                    <YAxis tickFormatter={eixoK} tick={{ fontSize: 11, fill: '#667085' }} axisLine={false} tickLine={false} width={64} />
                    <Tooltip content={<CustomTooltipDespesas />} cursor={{ fill: 'rgba(16,24,40,.04)' }} />
                    <Bar dataKey="desp_realizado" stackId="desp" name="Dentro do orçado" fill="#0b1f4b"
                      fillOpacity={op('desp_realizado', destaqueDespesa)}
                      shape={<BarraSegmento topoSe={(p) => !p.desp_ultrapassado && !p.desp_nao_realizado} />}
                      isAnimationActive={false}
                      onClick={(data) => abrirDetalhamentoMes(data, 'despesas')} style={{ cursor: 'pointer' }} />
                    <Bar dataKey="desp_ultrapassado" stackId="desp" name="Acima do orçado" fill="#c2410c"
                      fillOpacity={op('desp_ultrapassado', destaqueDespesa)}
                      shape={<BarraSegmento topoSe={(p) => !p.desp_nao_realizado} />}
                      isAnimationActive={false}
                      onClick={(data) => abrirDetalhamentoMes(data, 'despesas')} style={{ cursor: 'pointer' }} />
                    <Bar dataKey="desp_nao_realizado" stackId="desp" name="Orçado não realizado" fill="#e6e9ef"
                      fillOpacity={op('desp_nao_realizado', destaqueDespesa)}
                      shape={<BarraSegmento topoSe={() => true} tracejada />}
                      isAnimationActive={false}
                      onClick={(data) => abrirDetalhamentoMes(data, 'despesas')} style={{ cursor: 'pointer' }}>
                      <LabelList dataKey="desp_label" position="top" content={<RotuloTopo />} />
                    </Bar>
                    {orcadoMes > 0 && (
                      <ReferenceLine y={orcadoMes} stroke="#344054" strokeWidth={1.2} strokeDasharray="5 4"
                        label={{ value: `orçado ${rotuloK(orcadoMes)}/mês`, position: 'insideTopRight', fill: '#344054', fontSize: 11, fontWeight: 600 }} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <LegendaGrafico series={seriesDespesas} destaque={destaqueDespesa} setDestaque={setDestaqueDespesa} />
            </div>

            <div className="po-card">
              <div className="po-card-h">
                <div><h2>Receitas {anoSelecionado}</h2><div className="s">Meta × recebido por mês · clique no mês para o detalhe</div></div>
                <div className="r">
                  {mesesComMeta > 0
                    ? (mesesAcimaMeta > 0
                      ? <span className="po-pill good">{mesesAcimaMeta} {mesesAcimaMeta === 1 ? 'mês' : 'meses'} acima da meta</span>
                      : <span className="po-pill warn">abaixo da meta</span>)
                    : <span className="po-pill neutral">sem meta</span>}
                  <button type="button" className="po-btn ghost" onClick={() => navigate(`/Orcamento_receitas/${cliente_id}?ano=${anoSelecionado}`)}
                    title="Cadastrar a meta de receitas">
                    <i className="fas fa-sliders-h"></i>Cadastrar meta
                  </button>
                </div>
              </div>
              <div className="po-chart">
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={dadosReceitas} barCategoryGap="28%" margin={{ top: 22, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="var(--po-line)" />
                    <XAxis
                      dataKey="mes"
                      axisLine={false}
                      tickLine={false}
                      tick={(props) => {
                        const { x, y, payload } = props;
                        const mesData = dadosAno.meses_completos.find(m => m.mes === payload.value);
                        return (
                          <g transform={`translate(${x},${y})`} onClick={() => mesData && abrirDetalhamentoMes(mesData, 'receitas')} style={{ cursor: 'pointer' }}>
                            <text x={0} y={0} dy={14} textAnchor="middle" fill="#344054" fontSize={12} fontWeight={600}>{payload.value}</text>
                          </g>
                        );
                      }}
                    />
                    <YAxis tickFormatter={eixoK} tick={{ fontSize: 11, fill: '#667085' }} axisLine={false} tickLine={false} width={64} />
                    <Tooltip content={<CustomTooltipReceitas />} cursor={{ fill: 'rgba(16,24,40,.04)' }} />
                    <Bar dataKey="rec_atingido" stackId="rec" name="Meta recebida" fill="#0e7490"
                      fillOpacity={op('rec_atingido', destaqueReceita)}
                      shape={<BarraSegmento topoSe={(p) => !p.rec_acima && !p.rec_meta_rest} />}
                      isAnimationActive={false}
                      onClick={(data) => abrirDetalhamentoMes(data, 'receitas')} style={{ cursor: 'pointer' }} />
                    <Bar dataKey="rec_acima" stackId="rec" name="Acima da meta" fill="#15803d"
                      fillOpacity={op('rec_acima', destaqueReceita)}
                      shape={<BarraSegmento topoSe={(p) => !p.rec_meta_rest} />}
                      isAnimationActive={false}
                      onClick={(data) => abrirDetalhamentoMes(data, 'receitas')} style={{ cursor: 'pointer' }} />
                    <Bar dataKey="rec_meta_rest" stackId="rec" name="Meta não recebida" fill="#e6e9ef"
                      fillOpacity={op('rec_meta_rest', destaqueReceita)}
                      shape={<BarraSegmento topoSe={() => true} tracejada />}
                      isAnimationActive={false}
                      onClick={(data) => abrirDetalhamentoMes(data, 'receitas')} style={{ cursor: 'pointer' }}>
                      <LabelList dataKey="rec_label" position="top" content={<RotuloTopo />} />
                    </Bar>
                    {metaMes > 0 && (
                      <ReferenceLine y={metaMes} stroke="#344054" strokeWidth={1.2} strokeDasharray="5 4"
                        label={{ value: `meta ${rotuloK(metaMes)}/mês`, position: 'insideTopRight', fill: '#344054', fontSize: 11, fontWeight: 600 }} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <LegendaGrafico series={seriesReceitas} destaque={destaqueReceita} setDestaque={setDestaqueReceita} />
            </div>
          </section>
        )}

        {/* ANÁLISE IA — categorias que mais ultrapassam / economizam no ano */}
        {analiseIA && (analiseIA.ultrapassando?.length > 0 || analiseIA.economizando?.length > 0) && (
          <section className="po-card">
            <div className="po-card-h">
              <div><h2>Onde o ano saiu do plano</h2><div className="s">{analiseIA.periodo_label ? `Análise do acumulado ${analiseIA.periodo_label}, por categoria` : 'Análise do acumulado, por categoria'}</div></div>
              <span className="po-eyebrow"><i className="fas fa-robot" style={{ marginRight: 6 }}></i>Análise IA</span>
            </div>
            <div className="po-ia">
              <div>
                <h3><span className="po-pill bad">Ultrapassando</span> gastaram mais que o orçado</h3>
                {analiseIA.ultrapassando.length === 0
                  ? <div className="po-vazio">Nenhuma categoria acima do orçado no período.</div>
                  : <LinhasIA itens={analiseIA.ultrapassando} cor="var(--po-bar-over)" tom="po-bad" sinal="+" />}
              </div>
              <div>
                <h3><span className="po-pill good">Economizando</span> gastaram menos que o orçado</h3>
                {analiseIA.economizando.length === 0
                  ? <div className="po-vazio">Nenhuma economia relevante no período ainda.</div>
                  : <LinhasIA itens={analiseIA.economizando} cor="var(--po-bar-above)" tom="po-good" sinal="−" />}
              </div>
            </div>
          </section>
        )}

        {/* MODAL DETALHAMENTO DO MÊS */}
        {modalDetalhamento && mesDetalhado && dadosDetalheMes && (
          <div 
            className="modal fade show d-block" 
            style={{backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1060}}
            onClick={() => setModalDetalhamento(false)}
          >
            <div className="modal-dialog modal-dialog-scrollable" style={{maxWidth: '95vw'}} onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-calendar-alt me-2" style={{ color: '#212529' }}></i>
                    {mesDetalhado?.mes_ano?.endsWith('ytd')
                      ? `DRE Acumulado ${tipoDetalhamento === 'despesas' ? 'Despesas' : 'Receitas'}: ${periodoLabelYTD}`
                      : `Detalhamento ${tipoDetalhamento === 'despesas' ? 'Despesas' : 'Receitas'}: ${mesDetalhado.mes} - ${anoSelecionado}`
                    }
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setModalDetalhamento(false)}
                  ></button>
                </div>

                <div className="modal-header" style={{ background: tipoDetalhamento === 'despesas' ? 'linear-gradient(135deg, #fff5f5 0%, #ffe4e4 100%)' : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderBottom: `2px solid ${tipoDetalhamento === 'despesas' ? '#f5c6cb' : '#a7f3d0'}` }}>
                  <div className="w-100">
                    <div className="row text-center">
                      <div className="col-md-4">
                        <h6 className="text-muted small">Total Orçado</h6>
                        <h5 className={tipoDetalhamento === 'despesas' ? 'text-primary mb-0' : 'text-success mb-0'}>
                          {formatarValor(dadosDetalheMes.resumo.total_orcado)}
                        </h5>
                      </div>
                      <div className="col-md-4">
                        <h6 className="text-muted small">Total Realizado</h6>
                        <h5 className={tipoDetalhamento === 'despesas' ? 'text-danger mb-0' : 'text-success mb-0'}>
                          {formatarValor(dadosDetalheMes.resumo.total_realizado)}
                        </h5>
                      </div>
                      {tipoDetalhamento === 'despesas' && (
                        <div className="col-md-4">
                          <h6 className="text-muted small">Saldo</h6>
                          <h5 className={`mb-0 ${dadosDetalheMes.resumo.saldo >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatarValor(Math.abs(dadosDetalheMes.resumo.saldo))}
                          </h5>
                        </div>
                      )}
                    </div>

                  </div>
                </div>

                <div className="modal-body">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0" style={{ fontSize: '16px' }}>
                      <thead className="table-light" style={{ fontSize: '16px' }}>
                        {(() => {
                          const isYTD = mesDetalhado?.mes_ano?.endsWith('ytd');
                          return (
                            <tr>
                              <th width="30%">Categoria</th>
                              {!isYTD && <th width="13%" className="text-end">Orçado</th>}
                              <th width="13%" className="text-end">Realizado</th>
                              {!isYTD && tipoDetalhamento === 'despesas' && (
                                <>
                                  <th width="13%" className="text-end">Saldo</th>
                                  <th width="8%" className="text-center">% Exec.</th>
                                </>
                              )}
                            </tr>
                          );
                        })()}
                      </thead>
                      <tbody>
                        {(() => {
                          // Agrupar por DRE
                          const grupos = {};
                          [...dadosDetalheMes.por_categoria]
                            .sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true }))
                            .forEach(cat => {
                              const key = cat.codigo_dre || 'SEM_DRE';
                              if (!grupos[key]) {
                                grupos[key] = {
                                  codigo_dre: key,
                                  descricao_dre: cat.descricao_dre || 'Sem Classificação DRE',
                                  categorias: [],
                                  total_orcado: 0,
                                  total_realizado: 0,
                                };
                              }
                              grupos[key].categorias.push(cat);
                              grupos[key].total_orcado += cat.orcado;
                              grupos[key].total_realizado += cat.realizado;
                            });

                          return Object.values(grupos)
                            .sort((a, b) => a.codigo_dre.localeCompare(b.codigo_dre, undefined, { numeric: true }))
                            .map(grupo => {
                              const aberto = gruposAbertos.has(grupo.codigo_dre);
                              const saldoGrupo = grupo.total_orcado - grupo.total_realizado;

                              return (
                                <React.Fragment key={grupo.codigo_dre}>
                                  {/* Linha do grupo DRE */}
                                  <tr
                                    style={{ backgroundColor: '#e8ecf0', cursor: 'pointer', borderLeft: '4px solid #2c3e50' }}
                                    onClick={() => {
                                      setGruposAbertos(prev => {
                                        const next = new Set(prev);
                                        next.has(grupo.codigo_dre) ? next.delete(grupo.codigo_dre) : next.add(grupo.codigo_dre);
                                        return next;
                                      });
                                    }}
                                  >
                                    <td>
                                      <span style={{ color: '#2c3e50', fontWeight: 700, fontSize: '16px' }}>
                                        <i className={`fas fa-chevron-${aberto ? 'down' : 'right'} me-2`} style={{ fontSize: '11px' }}></i>
                                        {grupo.codigo_dre !== 'SEM_DRE' ? `${grupo.codigo_dre} — ` : ''}{grupo.descricao_dre}
                                      </span>
                                      <small style={{ color: '#666', marginLeft: '8px' }}>
                                        ({grupo.categorias.length} {grupo.categorias.length === 1 ? 'categoria' : 'categorias'})
                                      </small>
                                    </td>
                                    {!mesDetalhado?.mes_ano?.endsWith('ytd') && (
                                      <td className="text-end">
                                        <span style={{ color: '#2c3e50', fontWeight: 700 }}>{formatarValor(grupo.total_orcado)}</span>
                                      </td>
                                    )}
                                    <td className="text-end">
                                      <span style={{ color: '#2c3e50', fontWeight: 700 }}>{formatarValor(grupo.total_realizado)}</span>
                                    </td>
                                    {!mesDetalhado?.mes_ano?.endsWith('ytd') && tipoDetalhamento === 'despesas' && (
                                      <>
                                        <td className="text-end">
                                          {(() => {
                                            const saldo = saldoGrupo;
                                            if (saldo === 0) return <span style={{ color: '#27ae60', fontWeight: 700 }}>{formatarValor(0)}</span>;
                                            const cor = saldo > 0 ? '#27ae60' : '#e74c3c';
                                            const seta = saldo > 0 ? '▼' : '▲';
                                            const label = saldo > 0 ? 'Economia' : 'Excedeu';
                                            return (
                                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                                <div style={{ color: cor, fontWeight: 700, fontSize: '16px' }}>{seta} {formatarValor(Math.abs(saldo))}</div>
                                                <small style={{ color: cor, fontSize: '10px', fontWeight: 600 }}>{label}</small>
                                              </div>
                                            );
                                          })()}
                                        </td>
                                        <td></td>
                                      </>
                                    )}
                                  </tr>

                                  {/* Linhas das categorias (expandidas) */}
                                  {aberto && grupo.categorias.map((cat, idx) => {
                                    const percentual = tipoDetalhamento === 'despesas' && cat.orcado > 0
                                      ? (cat.realizado / cat.orcado) * 100
                                      : 0;
                                    return (
                                      <tr key={idx} className="categoria-aberta">
                                        <td style={{ paddingLeft: '32px' }}>
                                          <span
                                            style={{ color: '#2980b9', cursor: 'pointer', fontWeight: 700, textDecoration: 'underline' }}
                                            onClick={() => abrirLancamentosCategoria(cat)}
                                            title="Ver lançamentos"
                                          >
                                            {codCat(cat.codigo)} {cat.descricao}
                                          </span>
                                        </td>
                                        {!mesDetalhado?.mes_ano?.endsWith('ytd') && (
                                          <td className="text-end">
                                            <strong className={tipoDetalhamento === 'despesas' ? 'text-primary' : 'text-success'}>
                                              {formatarValor(cat.orcado)}
                                            </strong>
                                          </td>
                                        )}
                                        <td className="text-end">
                                          <strong className={tipoDetalhamento === 'despesas' ? 'text-danger' : 'text-success'}>
                                            {formatarValor(cat.realizado)}
                                          </strong>
                                        </td>
                                        {!mesDetalhado?.mes_ano?.endsWith('ytd') && tipoDetalhamento === 'despesas' && (
                                          <>
                                            <td className="text-end">
                                              {(() => {
                                                const saldo = cat.saldo;
                                                const cor = saldo >= 0 ? '#27ae60' : '#e74c3c';
                                                const seta = saldo > 0 ? '▼' : saldo < 0 ? '▲' : '';
                                                const label = saldo > 0 ? 'Economia' : saldo < 0 ? 'Excedeu' : 'Exato';
                                                return (
                                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                                    <div style={{ color: cor, fontWeight: 700, fontSize: '16px' }}>{seta} {formatarValor(Math.abs(saldo))}</div>
                                                    <small style={{ color: cor, fontSize: '10px', fontWeight: 600 }}>{label}</small>
                                                  </div>
                                                );
                                              })()}
                                            </td>
                                            <td className="text-center">
                                              <span
                                                className={`badge ${percentual > 100 ? 'bg-danger' : percentual > 80 ? 'bg-warning text-dark' : 'bg-success'}`}
                                                style={{ fontSize: '16px', padding: '5px 8px' }}
                                              >
                                                {percentual.toFixed(0)}%
                                              </span>
                                              <div className="progress mt-1" style={{ height: '4px' }}>
                                                <div
                                                  className={`progress-bar ${percentual > 100 ? 'bg-danger' : percentual > 80 ? 'bg-warning text-dark' : 'bg-success'}`}
                                                  style={{ width: `${Math.min(percentual, 100)}%` }}
                                                ></div>
                                              </div>
                                            </td>
                                          </>
                                        )}
                                      </tr>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="modal-footer">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setModalDetalhamento(false)}
                  >
                    <i className="fas fa-times me-2"></i>
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL LANÇAMENTOS DA CATEGORIA */}
        {modalLancamentos && categoriaLancamentos && (() => {
          const tipoLanc = categoriaLancamentos._tipo || tipoDetalhamento;
          const subtitulo = categoriaLancamentos._subtitulo || `${mesDetalhado?.mes} / ${anoSelecionado}`;
          return (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1070 }}
            onClick={() => setModalLancamentos(false)}
          >
            <div className="modal-dialog modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-list-ul me-2" style={{ color: '#212529' }}></i>
                      Lançamentos — {codCat(categoriaLancamentos.codigo)} {categoriaLancamentos.descricao}
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {subtitulo} — {tipoLanc === 'despesas' ? 'Saídas' : 'Entradas'}
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setModalLancamentos(false)}></button>
                </div>

                {carregandoLancamentos ? (
                  <div className="modal-body text-center py-5">
                    <div className="spinner-border text-primary mb-3"></div>
                    <p className="text-muted">Buscando lançamentos...</p>
                  </div>
                ) : dadosLancamentos ? (
                  <>
                    <div className="modal-header bg-light py-2">
                      <div className="w-100">
                        <div className="d-flex gap-4 w-100 justify-content-around text-center">
                          <div>
                            <small className="text-muted d-block">Lançamentos</small>
                            <strong>{dadosLancamentos.quantidade}</strong>
                          </div>
                          <div>
                            <small className="text-muted d-block">Total</small>
                            <strong className={tipoLanc === 'despesas' ? 'text-danger' : 'text-success'}>
                              {formatarValor(dadosLancamentos.total)}
                            </strong>
                          </div>
                          <div>
                            <small className="text-muted d-block">Orçado</small>
                            <strong className="text-primary">{formatarValor(categoriaLancamentos.orcado)}</strong>
                          </div>
                          {tipoLanc === 'despesas' && (categoriaLancamentos.orcado || 0) > 0 && (
                            <div>
                              <small className="text-muted d-block">Saldo</small>
                              <strong className={(categoriaLancamentos.orcado - dadosLancamentos.total) >= 0 ? 'text-success' : 'text-danger'}>
                                {formatarValor(categoriaLancamentos.orcado - dadosLancamentos.total)}
                              </strong>
                            </div>
                          )}
                        </div>

                      </div>
                    </div>

                    <div className="modal-body p-0">
                      {dadosLancamentos.lancamentos.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                          <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                          Nenhum lançamento encontrado no período para esta categoria.
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-hover mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>Fornecedor / Descrição</th>
                                <th width="130">Nº Documento</th>
                                <th width="120">Vencimento</th>
                                <th width="140" className="text-end">Valor</th>
                                <th width="110" className="text-center">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dadosLancamentos.lancamentos.map((lanc, idx) => {
                                return (
                                  <tr key={idx}>
                                    <td>
                                      <strong>{lanc.nome_fornecedor || '—'}</strong>
                                    </td>
                                    <td className="text-muted">{lanc.numero_documento || '—'}</td>
                                    <td>{lanc.data_vencimento ? new Date(lanc.data_vencimento).toLocaleDateString('pt-BR') : '—'}</td>
                                    <td className="text-end">
                                      <strong className={tipoLanc === 'despesas' ? 'text-danger' : 'text-success'}>
                                        {formatarValor(lanc.valor)}
                                      </strong>
                                    </td>
                                    <td className="text-center">
                                      <span className={`badge ${
                                        lanc.status === 'PAGO' || lanc.status === 'RECEBIDO' ? 'bg-success' :
                                        lanc.status === 'ATRASADO' ? 'bg-danger' : 'bg-secondary'
                                      }`}>
                                        {lanc.status || '—'}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                ) : null}

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setModalLancamentos(false)}>
                    <i className="fas fa-arrow-left me-2"></i>Voltar
                  </button>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {/* MODAL HISTÓRICO DO FORNECEDOR (componente compartilhado) */}
        <ModalHistoricoFornecedor
          aberto={modalHistorico}
          carregando={carregandoHistorico}
          dados={dadosHistorico}
          onFechar={() => setModalHistorico(false)}
          onNegociar={() => setModalEmailFornecedor(true)}
        />

        {/* MODAL EMAIL NEGOCIAÇÃO (componente compartilhado) */}
        <ModalEmailNegociacao
          aberto={modalEmailFornecedor}
          dadosHistorico={dadosHistorico}
          clienteId={cliente_id}
          nomeRemetente={dadosCliente?.nome}
          onFechar={() => setModalEmailFornecedor(false)}
          onMensagem={setMensagem}
        />

        {/* MODAL DRE COMPLETO */}
        {modalDRE && dadosDRE && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', transform: 'none' }}>
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: '70vw' }}>
              <div className="modal-content" style={{ fontFamily: 'inherit', borderRadius: 0, border: 'none', backgroundColor: '#ffffff' }}>

                {/* Cabeçalho */}
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef', borderRadius: 0, marginTop: '-1px' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-file-alt me-2" style={{ color: '#212529' }}></i>
                      DRE — Demonstração do Resultado do Exercício
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      Período: {dadosDRE.periodo_label}
                    </small>
                  </div>
                  <button className="btn-close" onClick={() => setModalDRE(false)}></button>
                </div>

                {/* Banner do critério de competência */}
                <div style={{ background: '#e7f0fb', borderBottom: '1px solid #b6d4f2', padding: '10px 24px', textAlign: 'center', fontSize: '15px', fontWeight: 700, color: '#00205b', letterSpacing: '0.2px' }}>
                  <i className="fas fa-calendar-check me-2"></i>
                  Competência por <span style={{ textDecoration: 'underline' }}>DATA DE VENCIMENTO</span>
                </div>

                {/* Filtro de meses */}
                <div style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef', padding: '12px 24px' }}>
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <i className="fas fa-calendar-alt" style={{ color: '#adb5bd', fontSize: '13px' }}></i>
                    {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'].map((m, i) => {
                      const mes = i + 1;
                      const ativo = mes >= mesInicioDRE && mes <= mesFimDRE;
                      return (
                        <button
                          key={mes}
                          onClick={() => {
                            let ini = mesInicioDRE, fim = mesFimDRE;
                            if (mes < mesInicioDRE) { ini = mes; }
                            else if (mes > mesFimDRE) { fim = mes; }
                            else if (mes === mesInicioDRE && mes !== mesFimDRE) { ini = mes + 1; }
                            else if (mes === mesFimDRE && mes !== mesInicioDRE) { fim = mes - 1; }
                            else { ini = mes; fim = mes; }
                            setMesInicioDRE(ini);
                            setMesFimDRE(fim);
                            buscarDRE(ini, fim);
                          }}
                          style={{
                            border: 'none',
                            borderRadius: '20px',
                            padding: '4px 10px',
                            fontSize: '12px',
                            fontWeight: ativo ? 700 : 400,
                            cursor: 'pointer',
                            backgroundColor: ativo ? '#00205b' : '#f1f3f5',
                            color: ativo ? '#ffffff' : '#868e96',
                            transition: 'all 0.15s',
                            minWidth: '38px',
                          }}
                        >
                          {m}
                        </button>
                      );
                    })}
                    {carregandoDREYTD && <span className="spinner-border spinner-border-sm ms-2" style={{ color: '#00205b', width: '14px', height: '14px' }}></span>}
                  </div>
                </div>

                <div className="modal-body p-0" style={{ backgroundColor: '#ffffff' }}>
                  <table className="table table-sm mb-0" style={{ fontSize: '18px' }}>
                    <colgroup>
                      <col style={{ width: '60%' }} />
                      <col style={{ width: '40%' }} />
                    </colgroup>
                    <tbody>

                      {/* ── RECEITAS ─────────────────────────────── */}
                      <tr style={{ backgroundColor: '#e8f5e9' }}>
                        <td colSpan={2} className="fw-bold py-2 px-3" style={{ color: '#1b5e20', fontSize: '18px', letterSpacing: '0.5px' }}>
                          (+) RECEITAS
                        </td>
                      </tr>

                      {dadosDRE.receitas.grupos.map((grupo) => (
                        <React.Fragment key={grupo.codigo_dre}>
                          <tr style={{ backgroundColor: '#f1f8f1' }}>
                            <td className="px-4 py-1 fw-semibold" style={{ color: '#2e7d32' }}>
                              {grupo.descricao_dre}
                            </td>
                            <td className="text-end px-4 py-1 fw-semibold" style={{ color: '#2e7d32' }}>
                              {formatarValor(grupo.total)}
                            </td>
                          </tr>
                          {grupo.categorias.map((cat, i) => {
                            const key = `receitas_${cat.codigo}`;
                            return (
                              <React.Fragment key={i}>
                                <tr
                                  style={{ backgroundColor: '#fff', cursor: 'pointer' }}
                                  onClick={() => toggleLancamentosDRE(cat, 'receitas')}
                                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0faf0'}
                                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
                                >
                                  <td className="px-5 py-1" style={{ fontSize: '16px', color: '#2e7d32' }}>
                                    <i className={`fas ${dreCatsExpandidas[key] ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`} style={{ fontSize: '11px', opacity: 0.5 }}></i>
                                    <span style={{ color: '#555' }}>{codCat(cat.codigo)}</span> {cat.descricao}
                                  </td>
                                  <td className="text-end px-4 py-1" style={{ fontSize: '16px', color: '#2e7d32' }}>
                                    {formatarValor(cat.total)}
                                  </td>
                                </tr>
                                {dreCatsExpandidas[key] && (
                                  <tr style={{ backgroundColor: '#f9fdf9' }}>
                                    <td colSpan={2} style={{ padding: 0, borderLeft: '4px solid #2e7d32' }}>
                                      {dreCarregando[key] ? (
                                        <div className="text-center py-2">
                                          <span className="spinner-border spinner-border-sm text-success"></span>
                                        </div>
                                      ) : (
                                        <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                                          <thead>
                                            <tr style={{ backgroundColor: '#e8f5e9' }}>
                                              <th className="px-5 py-1 text-start" style={{ fontWeight: '600', color: '#1b5e20' }}>Cliente / Pagador</th>
                                              <th className="px-3 py-1 text-start" style={{ fontWeight: '600', color: '#1b5e20' }}>Data</th>
                                              <th className="text-end px-4 py-1" style={{ fontWeight: '600', color: '#1b5e20' }}>Valor</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(dreLancamentos[key] || []).length === 0 ? (
                                              <tr><td colSpan={3} className="text-center py-2" style={{ color: '#888' }}>Nenhum lançamento encontrado.</td></tr>
                                            ) : (dreLancamentos[key] || []).map((lanc, li) => (
                                              <tr key={li} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                <td className="px-5 py-1">{lanc.nome_fornecedor || '—'}</td>
                                                <td className="px-3 py-1">{lanc.data_vencimento ? new Date(lanc.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</td>
                                                <td className="text-end px-4 py-1 fw-bold" style={{ color: '#2e7d32' }}>{formatarValor(lanc.valor)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      ))}

                      {/* Total Receitas */}
                      <tr style={{ backgroundColor: '#c8e6c9', borderTop: '2px solid #2e7d32' }}>
                        <td className="px-3 py-2 fw-bold" style={{ color: '#1b5e20', fontSize: '18px' }}>
                          (=) TOTAL RECEITAS
                        </td>
                        <td className="text-end px-4 py-2 fw-bold" style={{ color: '#1b5e20', fontSize: '20px' }}>
                          {formatarValor(dadosDRE.receitas.total)}
                        </td>
                      </tr>

                      {/* Espaçador */}
                      <tr><td colSpan={2} style={{ height: '12px', backgroundColor: '#f8f9fa' }}></td></tr>

                      {/* ── DESPESAS ─────────────────────────────── */}
                      <tr style={{ backgroundColor: '#fce4e4' }}>
                        <td colSpan={2} className="fw-bold py-2 px-3" style={{ color: '#7f0000', fontSize: '18px', letterSpacing: '0.5px' }}>
                          (-) DESPESAS
                        </td>
                      </tr>

                      {dadosDRE.despesas.grupos.map((grupo) => (
                        <React.Fragment key={grupo.codigo_dre}>
                          <tr style={{ backgroundColor: '#fff5f5' }}>
                            <td className="px-4 py-1 fw-semibold" style={{ color: '#c62828' }}>
                              {grupo.descricao_dre}
                            </td>
                            <td className="text-end px-4 py-1 fw-semibold" style={{ color: '#c62828' }}>
                              {formatarValor(grupo.total)}
                            </td>
                          </tr>
                          {grupo.categorias.map((cat, i) => {
                            const key = `despesas_${cat.codigo}`;
                            return (
                              <React.Fragment key={i}>
                                <tr
                                  style={{ backgroundColor: '#fff', cursor: 'pointer' }}
                                  onClick={() => toggleLancamentosDRE(cat, 'despesas')}
                                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fff5f5'}
                                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
                                >
                                  <td className="px-5 py-1" style={{ fontSize: '16px', color: '#c62828' }}>
                                    <i className={`fas ${dreCatsExpandidas[key] ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`} style={{ fontSize: '11px', opacity: 0.5 }}></i>
                                    <span style={{ color: '#555' }}>{codCat(cat.codigo)}</span> {cat.descricao}
                                  </td>
                                  <td className="text-end px-4 py-1" style={{ fontSize: '16px', color: '#c62828' }}>
                                    ({formatarValor(cat.total)})
                                  </td>
                                </tr>
                                {dreCatsExpandidas[key] && (
                                  <tr style={{ backgroundColor: '#fff8f8' }}>
                                    <td colSpan={2} style={{ padding: 0, borderLeft: '4px solid #c62828' }}>
                                      {dreCarregando[key] ? (
                                        <div className="text-center py-2">
                                          <span className="spinner-border spinner-border-sm text-danger"></span>
                                        </div>
                                      ) : (
                                        <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                                          <thead>
                                            <tr style={{ backgroundColor: '#fce4e4' }}>
                                              <th className="px-5 py-1 text-start" style={{ fontWeight: '600', color: '#7f0000' }}>Fornecedor</th>
                                              <th className="px-3 py-1 text-start" style={{ fontWeight: '600', color: '#7f0000' }}>Data</th>
                                              <th className="text-end px-4 py-1" style={{ fontWeight: '600', color: '#7f0000' }}>Valor</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(dreLancamentos[key] || []).length === 0 ? (
                                              <tr><td colSpan={3} className="text-center py-2" style={{ color: '#888' }}>Nenhum lançamento encontrado.</td></tr>
                                            ) : (dreLancamentos[key] || []).map((lanc, li) => (
                                              <tr key={li} style={{ borderBottom: '1px solid #f5e0e0' }}>
                                                <td className="px-5 py-1">{lanc.nome_fornecedor || '—'}</td>
                                                <td className="px-3 py-1">{lanc.data_vencimento ? new Date(lanc.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</td>
                                                <td className="text-end px-4 py-1 fw-bold" style={{ color: '#c62828' }}>({formatarValor(lanc.valor)})</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      ))}

                      {/* Total Despesas */}
                      <tr style={{ backgroundColor: '#ffcdd2', borderTop: '2px solid #c62828' }}>
                        <td className="px-3 py-2 fw-bold" style={{ color: '#7f0000', fontSize: '18px' }}>
                          (=) TOTAL DESPESAS
                        </td>
                        <td className="text-end px-4 py-2 fw-bold" style={{ color: '#7f0000', fontSize: '20px' }}>
                          ({formatarValor(dadosDRE.despesas.total)})
                        </td>
                      </tr>

                      {/* Espaçador */}
                      <tr><td colSpan={2} style={{ height: '12px', backgroundColor: '#f8f9fa' }}></td></tr>

                      {/* ── RESULTADO ────────────────────────────── */}
                      {(() => {
                        const res = dadosDRE.resultado;
                        const positivo = res >= 0;
                        const bgColor = positivo ? '#1b5e20' : '#7f0000';
                        return (
                          <React.Fragment>
                            <tr style={{ backgroundColor: bgColor }}>
                              <td className="px-3 py-3 fw-bold text-white" style={{ fontSize: '20px', letterSpacing: '0.5px' }}>
                                (=) RESULTADO DO PERÍODO
                                <small className="ms-3 opacity-75" style={{ fontSize: '14px' }}>
                                  {positivo ? '▲ Superávit' : '▼ Déficit'}
                                </small>
                              </td>
                              <td className="text-end px-4 py-3 fw-bold text-white" style={{ fontSize: '22px' }}>
                                {positivo ? '' : '('}{formatarValor(Math.abs(res))}{positivo ? '' : ')'}
                              </td>
                            </tr>
                            <tr><td colSpan={2} style={{ height: '8px', backgroundColor: '#f8f9fa' }}></td></tr>
                            <tr style={{ backgroundColor: positivo ? '#004d00' : '#4a0000', borderTop: `3px solid ${positivo ? '#00c853' : '#ff1744'}` }}>
                              <td className="px-3 py-4 fw-bold text-white" style={{ fontSize: '22px', letterSpacing: '1px' }}>
                                SALDO LÍQUIDO DO EXERCÍCIO
                              </td>
                              <td className="text-end px-4 py-4 fw-bold" style={{ fontSize: '26px', color: positivo ? '#69f0ae' : '#ff8a80' }}>
                                {positivo ? '' : '('}{formatarValor(Math.abs(res))}{positivo ? '' : ')'}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })()}

                    </tbody>
                  </table>
                </div>

                <div className="modal-footer" style={{ borderTop: '1px solid #dee2e6', backgroundColor: '#ffffff' }}>
                  <small className="text-muted me-auto">
                    <i className="fas fa-info-circle me-1"></i>
                    Valores realizados (PAGO / RECEBIDO) — {dadosDRE.periodo_label}
                  </small>
                  <button className="btn btn-secondary" onClick={() => setModalDRE(false)}>Fechar</button>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* MODAL ORÇADO x REALIZADO (todas as categorias, filtro mês a mês) */}
        {modalORxR && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setModalORxR(false)}>
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: '80vw' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-scale-balanced me-2" style={{ color: '#212529' }}></i>
                      Orçado x Realizado — Despesas
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {dadosORxR ? dadosORxR.periodo_label : 'Carregando...'}
                    </small>
                  </div>
                  <button className="btn-close" onClick={() => setModalORxR(false)}></button>
                </div>

                {/* Filtro de meses (igual ao DRE) */}
                <div style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef', padding: '12px 24px' }}>
                  <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                    <i className="fas fa-calendar-alt" style={{ color: '#adb5bd', fontSize: '13px' }}></i>
                    {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'].map((m, i) => {
                      const mes = i + 1;
                      const ativo = mes >= mesIniORxR && mes <= mesFimORxR;
                      return (
                        <button
                          key={mes}
                          onClick={() => {
                            let ini = mesIniORxR, fim = mesFimORxR;
                            if (mes < mesIniORxR) { ini = mes; }
                            else if (mes > mesFimORxR) { fim = mes; }
                            else if (mes === mesIniORxR && mes !== mesFimORxR) { ini = mes + 1; }
                            else if (mes === mesFimORxR && mes !== mesIniORxR) { fim = mes - 1; }
                            else { ini = mes; fim = mes; }
                            setMesIniORxR(ini);
                            setMesFimORxR(fim);
                            buscarORxR(ini, fim);
                          }}
                          style={{
                            border: 'none', borderRadius: '20px', padding: '4px 10px', fontSize: '12px',
                            fontWeight: ativo ? 700 : 400, cursor: 'pointer',
                            backgroundColor: ativo ? '#1b5e20' : '#f1f3f5', color: ativo ? '#fff' : '#868e96',
                            transition: 'all 0.15s', minWidth: '38px',
                          }}
                        >{m}</button>
                      );
                    })}
                    {carregandoORxR && <span className="spinner-border spinner-border-sm ms-2" style={{ color: '#1b5e20', width: '14px', height: '14px' }}></span>}
                  </div>
                </div>

                {/* Resumo */}
                {dadosORxR && (
                  <div className="modal-header bg-light py-2">
                    <div className="w-100">
                      <div className="row text-center">
                        <div className="col-4">
                          <h6 className="text-muted small mb-0">Total Orçado</h6>
                          <h5 className="text-primary mb-0">{formatarValor(dadosORxR.total_orcado)}</h5>
                        </div>
                        <div className="col-4">
                          <h6 className="text-muted small mb-0">Total Realizado</h6>
                          <h5 className="text-danger mb-0">{formatarValor(dadosORxR.total_realizado)}</h5>
                        </div>
                        <div className="col-4">
                          <h6 className="text-muted small mb-0">Saldo</h6>
                          <h5 className={`mb-0 ${(dadosORxR.total_orcado - dadosORxR.total_realizado) >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatarValor(dadosORxR.total_orcado - dadosORxR.total_realizado)}
                          </h5>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="modal-body p-0">
                  {!dadosORxR ? (
                    <div className="text-center py-5"><span className="spinner-border text-success"></span></div>
                  ) : dadosORxR.itens.length === 0 ? (
                    <div className="text-center py-5 text-muted"><i className="fas fa-inbox fa-3x mb-3 d-block"></i>Nenhuma categoria com orçamento ou realizado no período.</div>
                  ) : (
                    <table className="table table-hover mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          {[
                            { campo: 'codigo', rotulo: 'Categoria', cls: '' },
                            { campo: 'orcado', rotulo: 'Orçado', cls: 'text-end' },
                            { campo: 'realizado', rotulo: 'Realizado', cls: 'text-end' },
                            { campo: 'saldo', rotulo: 'Saldo', cls: 'text-end' },
                            { campo: 'pct', rotulo: '% Exec.', cls: 'text-center' },
                          ].map((col) => (
                            <th
                              key={col.campo}
                              className={col.cls}
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                              onClick={() => ordenarORxR(col.campo)}
                              title="Clique para ordenar (menor ↔ maior)"
                            >
                              {col.rotulo}
                              <span style={{ marginLeft: '6px', color: ordemORxR.campo === col.campo ? '#1b5e20' : '#c0c4cc' }}>
                                {ordemORxR.campo === col.campo ? (ordemORxR.dir === 'asc' ? '▲' : '▼') : '↕'}
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[...dadosORxR.itens].sort((a, b) => {
                          const { campo, dir } = ordemORxR;
                          let cmp;
                          if (campo === 'codigo' || campo === 'descricao') {
                            cmp = String(a[campo]).localeCompare(String(b[campo]), 'pt-BR', { numeric: true });
                          } else {
                            const va = a[campo] === null || a[campo] === undefined ? -Infinity : a[campo];
                            const vb = b[campo] === null || b[campo] === undefined ? -Infinity : b[campo];
                            cmp = va - vb;
                          }
                          return dir === 'asc' ? cmp : -cmp;
                        }).map((c) => {
                          const saldo = c.saldo;
                          const cor = saldo >= 0 ? '#27ae60' : '#e74c3c';
                          const seta = saldo > 0 ? '▼' : saldo < 0 ? '▲' : '';
                          const label = saldo > 0 ? 'Economia' : saldo < 0 ? 'Excedeu' : 'Exato';
                          const key = `orxr_${c.codigo}`;
                          const aberto = dreCatsExpandidas[key];
                          return (
                            <React.Fragment key={c.codigo}>
                              <tr
                                style={{ cursor: 'pointer' }}
                                onClick={() => toggleLancamentosInline(c, mesIniORxR, mesFimORxR, 'orxr')}
                                title="Ver lançamentos desta categoria no período"
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f8ff'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                              >
                                <td>
                                  <i className={`fas ${aberto ? 'fa-chevron-down' : 'fa-chevron-right'} me-2 text-muted`} style={{ fontSize: '11px', opacity: 0.5 }}></i>
                                  <strong className="text-primary">{codCat(c.codigo)}</strong> <span className="ms-2">{c.descricao}</span>
                                </td>
                                <td className="text-end">{formatarValor(c.orcado)}</td>
                                <td className="text-end fw-bold text-danger">{formatarValor(c.realizado)}</td>
                                <td className="text-end">
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                    <span style={{ color: cor, fontWeight: 700 }}>{seta} {formatarValor(Math.abs(saldo))}</span>
                                    <small style={{ color: cor, fontSize: '10px', fontWeight: 600 }}>{label}</small>
                                  </div>
                                </td>
                                <td className="text-center">
                                  {c.pct === null ? <span className="text-muted">—</span> : (
                                    <span className={`badge ${c.pct > 100 ? 'bg-danger' : c.pct > 80 ? 'bg-warning text-dark' : 'bg-success'}`} style={{ fontSize: '13px' }}>
                                      {c.pct.toFixed(0)}%
                                    </span>
                                  )}
                                </td>
                              </tr>
                              {aberto && (
                                <tr style={{ backgroundColor: '#fff8f8' }}>
                                  <td colSpan={5} style={{ padding: 0, borderLeft: '4px solid #c62828' }}>
                                    {dreCarregando[key] ? (
                                      <div className="text-center py-2"><span className="spinner-border spinner-border-sm text-danger"></span></div>
                                    ) : (
                                      <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                                        <thead>
                                          <tr style={{ backgroundColor: '#fce4e4' }}>
                                            <th className="px-5 py-1 text-start" style={{ fontWeight: 600, color: '#7f0000' }}>Fornecedor</th>
                                            <th className="px-3 py-1 text-start" style={{ fontWeight: 600, color: '#7f0000' }}>Nº Documento</th>
                                            <th className="px-3 py-1 text-start" style={{ fontWeight: 600, color: '#7f0000' }}>Data</th>
                                            <th className="text-end px-4 py-1" style={{ fontWeight: 600, color: '#7f0000' }}>Valor</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(dreLancamentos[key] || []).length === 0 ? (
                                            <tr><td colSpan={4} className="text-center py-2" style={{ color: '#888' }}>Nenhum lançamento encontrado.</td></tr>
                                          ) : (dreLancamentos[key] || []).map((lanc, li) => (
                                            <tr key={li} style={{ borderBottom: '1px solid #f5e0e0' }}>
                                              <td className="px-5 py-1">{lanc.nome_fornecedor || '—'}</td>
                                              <td className="px-3 py-1 text-muted">{lanc.numero_documento || '—'}</td>
                                              <td className="px-3 py-1">{lanc.data_vencimento ? new Date(lanc.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</td>
                                              <td className="text-end px-4 py-1 fw-bold" style={{ color: '#c62828' }}>{formatarValor(lanc.valor)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setModalORxR(false)}>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL GASTOS NÃO ORÇADOS (realizado sem orçamento cadastrado) */}
        {modalGNO && (() => {
          const naoOrcados = (dadosGNO?.itens || [])
            .filter(i => (i.orcado || 0) <= 0 && (i.realizado || 0) > 0)
            .sort((a, b) => b.realizado - a.realizado);
          const totalNaoOrcado = naoOrcados.reduce((s, i) => s + i.realizado, 0);
          return (
            <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setModalGNO(false)}>
              <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: '70vw' }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-content">
                  <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                    <div>
                      <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                        <i className="fas fa-triangle-exclamation me-2" style={{ color: '#b91c1c' }}></i>
                        Gastos não orçados
                      </h5>
                      <small style={{ color: '#6c757d' }}>
                        Despesas realizadas sem orçamento cadastrado{dadosGNO ? ` — ${dadosGNO.periodo_label}` : ''}
                      </small>
                    </div>
                    <button className="btn-close" onClick={() => setModalGNO(false)}></button>
                  </div>

                  {/* Filtro de meses */}
                  <div style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef', padding: '12px 24px' }}>
                    <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                      <i className="fas fa-calendar-alt" style={{ color: '#adb5bd', fontSize: '13px' }}></i>
                      {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'].map((m, i) => {
                        const mes = i + 1;
                        const ativo = mes >= mesIniGNO && mes <= mesFimGNO;
                        return (
                          <button
                            key={mes}
                            onClick={() => {
                              let ini = mesIniGNO, fim = mesFimGNO;
                              if (mes < mesIniGNO) { ini = mes; }
                              else if (mes > mesFimGNO) { fim = mes; }
                              else if (mes === mesIniGNO && mes !== mesFimGNO) { ini = mes + 1; }
                              else if (mes === mesFimGNO && mes !== mesIniGNO) { fim = mes - 1; }
                              else { ini = mes; fim = mes; }
                              setMesIniGNO(ini);
                              setMesFimGNO(fim);
                              buscarGNO(ini, fim);
                            }}
                            style={{
                              border: 'none', borderRadius: '20px', padding: '4px 10px', fontSize: '12px',
                              fontWeight: ativo ? 700 : 400, cursor: 'pointer',
                              backgroundColor: ativo ? '#b91c1c' : '#f1f3f5', color: ativo ? '#fff' : '#868e96',
                              transition: 'all 0.15s', minWidth: '38px',
                            }}
                          >{m}</button>
                        );
                      })}
                      {carregandoGNO && <span className="spinner-border spinner-border-sm ms-2" style={{ color: '#b91c1c', width: '14px', height: '14px' }}></span>}
                    </div>
                  </div>

                  {dadosGNO && (
                    <div className="modal-header bg-light py-2">
                      <div className="w-100 d-flex justify-content-around text-center">
                        <div>
                          <h6 className="text-muted small mb-0">Categorias sem orçamento</h6>
                          <h5 className="mb-0" style={{ color: '#b91c1c' }}>{naoOrcados.length}</h5>
                        </div>
                        <div>
                          <h6 className="text-muted small mb-0">Total gasto sem orçamento</h6>
                          <h5 className="text-danger mb-0">{formatarValor(totalNaoOrcado)}</h5>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="modal-body p-0">
                    {!dadosGNO ? (
                      <div className="text-center py-5"><span className="spinner-border text-warning"></span></div>
                    ) : naoOrcados.length === 0 ? (
                      <div className="text-center py-5 text-muted"><i className="fas fa-check-circle fa-3x mb-3 d-block text-success"></i>Todos os gastos do período têm orçamento cadastrado.</div>
                    ) : (
                      <table className="table table-hover mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>Categoria</th>
                            <th className="text-end">Realizado (sem orçamento)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {naoOrcados.map((c) => {
                            const key = `gno_${c.codigo}`;
                            const aberto = dreCatsExpandidas[key];
                            return (
                              <React.Fragment key={c.codigo}>
                                <tr
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => toggleLancamentosInline(c, mesIniGNO, mesFimGNO, 'gno')}
                                  title="Ver lançamentos desta categoria no período"
                                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fff5f5'}
                                  onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                                >
                                  <td>
                                    <i className={`fas ${aberto ? 'fa-chevron-down' : 'fa-chevron-right'} me-2 text-muted`} style={{ fontSize: '11px', opacity: 0.5 }}></i>
                                    <strong className="text-primary">{codCat(c.codigo)}</strong> <span className="ms-2">{c.descricao}</span>
                                  </td>
                                  <td className="text-end fw-bold text-danger">{formatarValor(c.realizado)}</td>
                                </tr>
                                {aberto && (
                                  <tr style={{ backgroundColor: '#fff8f8' }}>
                                    <td colSpan={2} style={{ padding: 0, borderLeft: '4px solid #c62828' }}>
                                      {dreCarregando[key] ? (
                                        <div className="text-center py-2"><span className="spinner-border spinner-border-sm text-danger"></span></div>
                                      ) : (
                                        <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                                          <thead>
                                            <tr style={{ backgroundColor: '#fce4e4' }}>
                                              <th className="px-5 py-1 text-start" style={{ fontWeight: 600, color: '#7f0000' }}>Fornecedor</th>
                                              <th className="px-3 py-1 text-start" style={{ fontWeight: 600, color: '#7f0000' }}>Nº Documento</th>
                                              <th className="px-3 py-1 text-start" style={{ fontWeight: 600, color: '#7f0000' }}>Data</th>
                                              <th className="text-end px-4 py-1" style={{ fontWeight: 600, color: '#7f0000' }}>Valor</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(dreLancamentos[key] || []).length === 0 ? (
                                              <tr><td colSpan={4} className="text-center py-2" style={{ color: '#888' }}>Nenhum lançamento encontrado.</td></tr>
                                            ) : (dreLancamentos[key] || []).map((lanc, li) => (
                                              <tr key={li} style={{ borderBottom: '1px solid #f5e0e0' }}>
                                                <td className="px-5 py-1">{lanc.nome_fornecedor || '—'}</td>
                                                <td className="px-3 py-1 text-muted">{lanc.numero_documento || '—'}</td>
                                                <td className="px-3 py-1">{lanc.data_vencimento ? new Date(lanc.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</td>
                                                <td className="text-end px-4 py-1 fw-bold" style={{ color: '#c62828' }}>{formatarValor(lanc.valor)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setModalGNO(false)}>Fechar</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* MODAL DRE HORIZONTAL (pivot estilo Omie) */}
        {modalDREH && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setModalDREH(false)}>
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: '96vw' }} onClick={e => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-table-cells me-2" style={{ color: '#212529' }}></i>DRE Horizontal — {anoSelecionado}
                    </h5>
                    <small style={{ color: '#6c757d' }}>Tipo › Grupo › Conta do DRE › Categoria — valores por mês (clique nas linhas para recolher)</small>
                  </div>
                  <button className="btn-close" onClick={() => setModalDREH(false)}></button>
                </div>

                {/* Banner do critério de competência */}
                <div style={{ background: '#e7f0fb', borderBottom: '1px solid #b6d4f2', padding: '10px 24px', textAlign: 'center', fontSize: '15px', fontWeight: 700, color: '#00205b', letterSpacing: '0.2px' }}>
                  <i className="fas fa-calendar-check me-2"></i>
                  Competência por <span style={{ textDecoration: 'underline' }}>DATA DE VENCIMENTO</span>
                </div>

                {/* Filtro de meses (igual ao DRE) */}
                <div style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef', padding: '14px 24px' }}>
                  <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                    <i className="fas fa-calendar-alt" style={{ color: '#adb5bd', fontSize: '15px' }}></i>
                    {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'].map((m, i) => {
                      const mes = i + 1;
                      const ativo = mes >= mesIniDREH && mes <= mesFimDREH;
                      return (
                        <button
                          key={mes}
                          onClick={() => {
                            let ini = mesIniDREH, fim = mesFimDREH;
                            if (mes < mesIniDREH) { ini = mes; }
                            else if (mes > mesFimDREH) { fim = mes; }
                            else if (mes === mesIniDREH && mes !== mesFimDREH) { ini = mes + 1; }
                            else if (mes === mesFimDREH && mes !== mesIniDREH) { fim = mes - 1; }
                            else { ini = mes; fim = mes; }
                            setMesIniDREH(ini);
                            setMesFimDREH(fim);
                            buscarDREH(ini, fim);
                          }}
                          style={{
                            border: 'none', borderRadius: '20px', padding: '6px 14px', fontSize: '14px',
                            fontWeight: ativo ? 700 : 400, cursor: 'pointer',
                            backgroundColor: ativo ? '#00205b' : '#f1f3f5', color: ativo ? '#fff' : '#868e96',
                            transition: 'all 0.15s', minWidth: '46px',
                          }}
                        >{m}</button>
                      );
                    })}
                    {carregandoDREH && <span className="spinner-border spinner-border-sm ms-2" style={{ color: '#00205b', width: '16px', height: '16px' }}></span>}
                  </div>
                </div>

                <div className="modal-body p-0">
                  {carregandoDREH ? (
                    <div className="text-center py-5"><span className="spinner-border text-primary"></span><p className="text-muted mt-2">Gerando DRE...</p></div>
                  ) : !dadosDREH ? null : dadosDREH.erro ? (
                    <div className="alert alert-warning m-4 mb-0">{dadosDREH.erro}</div>
                  ) : (dadosDREH.tipos || []).length === 0 ? (
                    <div className="text-center py-5 text-muted"><i className="fas fa-inbox fa-3x mb-3 d-block"></i>Sem movimentos no ano.</div>
                  ) : (() => {
                    const meses = dadosDREH.meses;
                    const labels = dadosDREH.meses_labels;
                    const linhas = [];
                    const walk = (node, nivel) => {
                      const temFilhos = Array.isArray(node.filhos) && node.filhos.length > 0;
                      linhas.push({ node, nivel, temFilhos });
                      if (temFilhos && !dreHColapsados.has(node.codigo)) {
                        node.filhos.forEach(f => walk(f, nivel + 1));
                      }
                    };
                    dadosDREH.tipos.forEach(t => walk(t, 0));

                    const cellVal = (v) => {
                      if (!v) return null;
                      return <span style={{ color: v < 0 ? '#c62828' : '#1b5e20' }}>{formatarValor(v)}</span>;
                    };

                    const estilosNivel = [
                      { bg: '#e8ecf2', fw: 700, cor: '#00205b' },
                      { bg: '#eef1f5', fw: 600, cor: '#243b6b' },
                      { bg: '#f6f8fa', fw: 600, cor: '#34495e' },
                      { bg: '#ffffff', fw: 400, cor: '#2c3e50' },
                    ];

                    return (
                      <div style={{ overflowX: 'auto' }}>
                        <table className="mb-0" style={{ borderCollapse: 'collapse', width: '100%', fontSize: '16px', whiteSpace: 'nowrap' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#00205b', color: '#fff', fontSize: '14px', letterSpacing: '0.4px' }}>
                              <th style={{ padding: '14px 8px', textAlign: 'left', width: '40px', minWidth: '40px', position: 'sticky', left: 0, backgroundColor: '#00205b' }}>TIPO</th>
                              <th style={{ padding: '14px 8px', textAlign: 'left', width: '64px', minWidth: '64px' }}>GRUPO</th>
                              <th style={{ padding: '14px 8px', textAlign: 'left', width: '80px', minWidth: '80px' }}>CONTA DO DRE</th>
                              <th style={{ padding: '14px 8px', textAlign: 'left', width: '110px', minWidth: '110px' }}>CATEGORIA</th>
                              {labels.map((l, i) => <th key={i} style={{ padding: '14px 16px', textAlign: 'right', minWidth: '125px' }}>{l}</th>)}
                              <th style={{ padding: '14px 16px', textAlign: 'right', minWidth: '135px', borderLeft: '2px solid rgba(255,255,255,.25)' }}>Totais</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* espaçador entre o cabeçalho e a tabela */}
                            <tr style={{ height: '16px' }}><td colSpan={5 + meses.length} style={{ border: 'none', background: '#fff' }}></td></tr>
                            {linhas.map(({ node, nivel, temFilhos }, idx) => {
                              const est = estilosNivel[nivel];
                              const colapsado = dreHColapsados.has(node.codigo);
                              const cols = ['', '', '', ''];
                              cols[nivel] = node.label;
                              const ehFolha = !temFilhos;
                              const lkey = `dreh_${node.codigo}`;
                              const lancAberto = ehFolha && dreCatsExpandidas[lkey];
                              return (
                                <React.Fragment key={node.codigo + '_' + idx}>
                                <tr
                                    style={{ backgroundColor: est.bg, cursor: 'pointer', borderBottom: '1px solid #eceef1' }}
                                    onClick={temFilhos ? () => toggleDREH(node.codigo) : () => toggleLancamentosDREH(node.codigo)}
                                    title={ehFolha ? 'Ver lançamentos desta categoria' : undefined}>
                                  {cols.map((c, ci) => (
                                    <td key={ci} style={{
                                      padding: '9px 8px',
                                      fontWeight: ci === nivel ? est.fw : 400,
                                      color: est.cor,
                                      paddingLeft: ci === nivel ? `${8 + nivel * 8}px` : '8px',
                                      position: ci === 0 ? 'sticky' : undefined,
                                      left: ci === 0 ? 0 : undefined,
                                      backgroundColor: ci === 0 ? est.bg : undefined,
                                    }}>
                                      {ci === nivel && c ? (
                                        <>
                                          {temFilhos
                                            ? <i className={`fas fa-chevron-${colapsado ? 'right' : 'down'} me-2`} style={{ fontSize: '12px', opacity: .5 }}></i>
                                            : <i className={`fas fa-chevron-${lancAberto ? 'down' : 'right'} me-2`} style={{ fontSize: '11px', opacity: .35 }}></i>}
                                          {c}
                                        </>
                                      ) : ''}
                                    </td>
                                  ))}
                                  {meses.map((m) => (
                                    <td key={m} style={{ padding: '9px 16px', textAlign: 'right', fontWeight: nivel < 3 ? 600 : 400 }}>
                                      {cellVal(node.valores[String(m)])}
                                    </td>
                                  ))}
                                  <td style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 700, borderLeft: '2px solid #e3e6ea' }}>
                                    {cellVal(node.total)}
                                  </td>
                                </tr>
                                {lancAberto && (
                                  <tr>
                                    <td colSpan={5 + meses.length} style={{ padding: 0, background: '#f7f9fc', borderLeft: '4px solid #00205b' }}>
                                      {dreCarregando[lkey] ? (
                                        <div className="text-center py-2"><span className="spinner-border spinner-border-sm" style={{ color: '#00205b' }}></span></div>
                                      ) : (
                                        <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                                          <thead>
                                            <tr style={{ backgroundColor: '#e7f0fb', color: '#00205b' }}>
                                              <th className="px-4 py-1 text-start" style={{ fontWeight: 600 }}>Data</th>
                                              <th className="px-3 py-1 text-start" style={{ fontWeight: 600 }}>Fornecedor / Cliente / Descrição</th>
                                              <th className="px-3 py-1 text-start" style={{ fontWeight: 600 }}>Documento</th>
                                              <th className="px-3 py-1 text-start" style={{ fontWeight: 600 }}>Status</th>
                                              <th className="text-end px-4 py-1" style={{ fontWeight: 600 }}>Valor</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(dreLancamentos[lkey] || []).length === 0 ? (
                                              <tr><td colSpan={5} className="text-center py-2" style={{ color: '#888' }}>Nenhum lançamento no período.</td></tr>
                                            ) : (dreLancamentos[lkey] || []).map((lc, li) => (
                                              <tr key={li} style={{ borderBottom: '1px solid #eef1f5' }}>
                                                <td className="px-4 py-1">{lc.data ? new Date(lc.data + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</td>
                                                <td className="px-3 py-1">{lc.nome || '—'}</td>
                                                <td className="px-3 py-1 text-muted">{lc.numero_documento || '—'}</td>
                                                <td className="px-3 py-1"><span className="badge bg-light text-dark" style={{ fontWeight: 500 }}>{lc.status || '—'}</span></td>
                                                <td className="text-end px-4 py-1 fw-bold" style={{ color: lc.valor < 0 ? '#c62828' : '#1b5e20' }}>{formatarValor(lc.valor)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                    </td>
                                  </tr>
                                )}
                                </React.Fragment>
                              );
                            })}
                            <tr style={{ backgroundColor: '#00205b', color: '#fff', fontWeight: 700, fontSize: '16px' }}>
                              <td colSpan={4} style={{ padding: '13px 16px', position: 'sticky', left: 0, backgroundColor: '#00205b' }}>RESULTADO LÍQUIDO</td>
                              {meses.map(m => (
                                <td key={m} style={{ padding: '13px 16px', textAlign: 'right' }}>
                                  {dadosDREH.totais_mes[String(m)] ? formatarValor(dadosDREH.totais_mes[String(m)]) : ''}
                                </td>
                              ))}
                              <td style={{ padding: '13px 16px', textAlign: 'right', borderLeft: '2px solid rgba(255,255,255,.25)' }}>
                                {formatarValor(dadosDREH.total_geral)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setModalDREH(false)}>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL HISTÓRICO DE ORÇAMENTO */}
        {modalHistoricoOrcamento && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-history me-2" style={{ color: '#212529' }}></i>
                    Histórico de Alterações de Orçamento — {anoSelecionado}
                  </h5>
                  <button className="btn-close" onClick={() => setModalHistoricoOrcamento(false)}></button>
                </div>
                <div className="modal-body">
                  {carregandoHistoricoOrc ? (
                    <div className="text-center py-5">
                      <span className="spinner-border text-primary"></span>
                      <p className="mt-2 text-muted">Carregando histórico...</p>
                    </div>
                  ) : historicoOrcamento.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                      Nenhuma alteração registrada para {anoSelecionado}.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover table-sm align-middle">
                        <thead className="table-dark">
                          <tr>
                            <th>Data / Hora</th>
                            <th>Usuário</th>
                            <th>Categoria</th>
                            <th>Período</th>
                            <th>Mês Ref.</th>
                            <th className="text-end">Novo Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historicoOrcamento.map((h) => (
                            <tr key={h.id}>
                              <td style={{ whiteSpace: 'nowrap' }}>{h.data_alteracao}</td>
                              <td>{h.usuario}</td>
                              <td>
                                <span className="text-primary fw-bold me-2">{h.codigo_categoria}</span>
                                {h.descricao_categoria}
                              </td>
                              <td>
                                <span className={`badge ${h.periodo === 'ano' ? 'bg-primary' : 'bg-secondary'}`}>
                                  {h.periodo === 'ano' ? 'Ano Todo' : 'Mês Específico'}
                                </span>
                              </td>
                              <td>{h.mes_referencia || '—'}</td>
                              <td className="text-end fw-bold">{formatarValor(h.novo_valor)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setModalHistoricoOrcamento(false)}>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

export default PlanejamentoCliente;